import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';
import Pagination from '../common/Pagination';
import CommunityIdentity from '../common/CommunityIdentity';
import './finance.css';
const today = () => new Date().toISOString().slice(0, 10);
const money = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
    Number(v || 0)
  );
const dateTime = (value) =>
  value
    ? new Intl.DateTimeFormat('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'medium',
        timeZone: 'Asia/Kolkata',
      }).format(new Date(`${value}${/Z|[+-]\d\d:\d\d$/.test(value) ? '' : 'Z'}`))
    : 'Not recorded';
const FINANCE_FIELD_HELP = {
  Community:
    'Choose the community whose collections, accounts, totals and reports you want to manage. No data is shared between communities.',
  Title:
    'Enter a short name that clearly identifies this collection or cashbook entry in resident screens and reports.',
  Description:
    'Explain why the money is being collected or spent. Residents and administrators can use this for context.',
  Type:
    'Choose the business purpose of the collection, such as maintenance, repair, festival or donation. This helps classify reports.',
  Mode:
    'Choose how amounts are assigned. Fixed per unit charges the same amount to every selected home; common amount represents one shared amount; custom or manual modes allow individual assignment.',
  'Contribution requirement':
    'Compulsory creates a payable due for each selected property. Voluntary publishes an optional contribution and does not create an outstanding bill.',
  'Who should receive it?':
    'Choose the audience: every property in the community, selected divisions or wings, or only selected homes.',
  'Select homes':
    'Select one or more specific properties that should receive this collection. Hold Ctrl (Windows) or Command (Mac) to select multiple homes.',
  'Accounting unit':
    'The part of the community that keeps these financial records. Most communities need only one unit named Whole Community.',
  Fund:
    'The purpose bucket that receives or spends the money, for example Monthly Maintenance, Repairs or Navratri Event.',
  Start:
    'The date from which this collection becomes applicable.',
  Due:
    'The last date residents should pay. Leave it blank only when the contribution has no fixed deadline.',
  'Amount billed to each property':
    'The amount that will be charged separately to every eligible property when compulsory charges are generated.',
  'Minimum contribution (optional)':
    'The smallest suggested or permitted voluntary amount. Leave blank when residents may enter any positive amount.',
  'Maximum contribution (optional)':
    'The largest voluntary amount accepted from one property. Leave blank when there is no upper limit.',
  'Entry type':
    'Choose Income when money enters the community account and Expense when money is paid out.',
  Account:
    'Select the community bank, UPI or cash account through which this transaction was received or paid.',
  Category:
    'Enter a reporting category such as Electricity, Security, Repair or Interest.',
  Date:
    'The actual date on which the income or expense transaction took place.',
  Party:
    'Enter the person, vendor or organisation that paid or received the money.',
  Amount:
    'Enter the actual amount paid, received or submitted. Use rupees and paise without currency symbols.',
  'Campaign and home':
    'Choose the collection and the property for which you are submitting payment details.',
  'Payment account':
    'Choose the community bank or UPI destination to which you made the payment.',
  'Payment mode':
    'Select how the payment was made, such as UPI, bank transfer or cheque.',
  'Transaction reference':
    'Enter the bank UTR, UPI reference, cheque reference or another identifier that the administrator can verify.',
  'Invoice / source evidence':
    'Attach the vendor invoice, bill, receipt or supporting document. PNG, JPEG and PDF files are accepted.',
  'Payment proof':
    'Attach the final bank advice, UPI receipt, cheque proof or signed cash voucher after the expense is paid.',
  'Payment date':
    'Enter the date on which money actually left the community account or cash book.',
  'Payment remarks':
    'Add an optional note explaining the payment, approval reference or any exception.',
  'Payer name':
    'Enter the name shown on the bank account, UPI transfer or cheque used for this payment.',
  'Payment screenshot / transfer proof (optional)':
    'Attach a PNG, JPEG or PDF receipt so the community administrator can verify the payment more easily.',
  Code:
    'Enter a short unique internal code, for example WHOLE, MAINT or NAVRATRI. Use uppercase letters and avoid changing it after transactions exist.',
  Name:
    'Enter a clear name that administrators will recognise in forms and reports.',
  'Account name':
    'Enter a friendly name for the receiving destination, for example Society Bank Account or Maintenance UPI.',
  'UPI ID':
    'Enter the community receiving UPI address, for example society@bank. This is shown to eligible residents as a payment instruction.',
  'Masked bank account':
    'Show only a safe, recognisable form of the bank account, such as XXXX1234. Do not enter the complete account number here.',
  'Payment QR (PNG/JPEG)':
    'Upload the official payment QR for this receiving account. Residents can use it as a payment instruction; PNG and JPEG files are accepted.',
};

function FinanceFieldLabel({ label, help }) {
  const description = help || FINANCE_FIELD_HELP[label];
  const iconRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const showTooltip = () => {
    const bounds = iconRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const width = Math.min(320, window.innerWidth - 24);
    const left = Math.max(
      12,
      Math.min(bounds.left + bounds.width / 2 - width / 2, window.innerWidth - width - 12)
    );
    const above = bounds.top > 145;
    setTooltip({
      left,
      top: above ? bounds.top - 10 : bounds.bottom + 10,
      width,
      above,
    });
  };
  return (
    <span className="finance-field-title">
      {label}
      {description && (
        <>
          <span
            ref={iconRef}
            className="finance-help-icon"
            tabIndex="0"
            role="button"
            aria-label={`${label}: ${description}`}
            onMouseEnter={showTooltip}
            onMouseLeave={() => setTooltip(null)}
            onFocus={showTooltip}
            onBlur={() => setTooltip(null)}
            onClick={() => (tooltip ? setTooltip(null) : showTooltip())}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setTooltip(null);
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                tooltip ? setTooltip(null) : showTooltip();
              }
            }}
          >
            ?
          </span>
          {tooltip &&
            createPortal(
              <span
                role="tooltip"
                className={`finance-tooltip ${
                  tooltip.above ? 'finance-tooltip-above' : 'finance-tooltip-below'
                }`}
                style={{
                  left: tooltip.left,
                  top: tooltip.top,
                  width: tooltip.width,
                }}
              >
                <strong>{label}</strong>
                <span>{description}</span>
              </span>,
              document.body
            )}
        </>
      )}
    </span>
  );
}

const blankCampaign = {
  title: '',
  description: '',
  collectionType: 'MAINTENANCE',
  collectionMode: 'FIXED_PER_UNIT',
  billingScope: 'COMMUNITY',
  contributionType: 'COMPULSORY',
  structureUnitIds: [],
  propertyIds: [],
  startDate: today(),
  dueDate: '',
  amountPerUnit: '',
  minimumContribution: '',
  maximumContribution: '',
  status: 'DRAFT',
  accountingUnitId: '',
  fundId: '',
};
const financeReportTypes = [
  ['collections', 'Collections'],
  ['outstanding', 'Outstanding dues'],
  ['payments', 'Payments'],
  ['income', 'Income'],
  ['expense', 'Expenses'],
  ['cashbook', 'Cashbook'],
  ['receipts', 'Receipts'],
];
export default function CommunityFinance() {
  const { session } = useContext(AuthContext);
  const roles = session?.roles || [];
  const superAdmin = roles.includes('SUPER_ADMIN');
  const admin = roles.some((role) => ['ADMIN', 'SUPER_ADMIN'].includes(role));
  const resident = roles.includes('USER');
  const { get, post, put, delete: remove } = useApi();
  const [tab, setTab] = useState(admin ? 'overview' : 'dues'),
    [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [foundation, setFoundation] = useState({
      accountingUnits: [],
      unitStructureMappings: [],
      funds: [],
      transactionAccounts: [],
      accountPaymentModes: [],
      scopeAssignments: [],
    }),
    [residentAccounts, setResidentAccounts] = useState([]),
    [structureUnits, setStructureUnits] = useState([]),
    [properties, setProperties] = useState([]),
    [summary, setSummary] = useState({}),
    [campaigns, setCampaigns] = useState({ content: [], totalPages: 0 }),
    [residentCampaigns, setResidentCampaigns] = useState({
      content: [],
      totalPages: 0,
    }),
    [dues, setDues] = useState({ content: [], totalPages: 0 }),
    [paymentHistory, setPaymentHistory] = useState({ content: [], totalPages: 0 }),
    [paymentHistoryPage, setPaymentHistoryPage] = useState(0),
    [paymentHistoryFilters, setPaymentHistoryFilters] = useState({
      status: 'ALL',
      search: '',
      from: '',
      to: '',
      size: 5,
    }),
    [queue, setQueue] = useState({ content: [], totalPages: 0 }),
    [queueFilters, setQueueFilters] = useState({
      status: 'PENDING',
      search: '',
      from: '',
      to: '',
      size: 10,
    }),
    [campaignPage, setCampaignPage] = useState(0),
    [campaignFilters, setCampaignFilters] = useState({
      status: 'ALL',
      search: '',
      collectionType: 'ALL',
      contributionType: 'ALL',
      from: '',
      to: '',
      size: 5,
    }),
    [selectedVerification, setSelectedVerification] = useState(null),
    [chargePreview, setChargePreview] = useState(null),
    [cashbook, setCashbook] = useState({ content: [], totalPages: 0 }),
    [cashbookView, setCashbookView] = useState('register'),
    [cashbookPage, setCashbookPage] = useState(0),
    [cashbookFilters, setCashbookFilters] = useState({
      type: 'ALL',
      status: 'ALL',
      search: '',
      from: '',
      to: '',
      sortBy: 'transactionDate',
      direction: 'desc',
      size: 5,
    }),
    [selectedCashbookEntry, setSelectedCashbookEntry] = useState(null),
    [showOnBehalfPayment, setShowOnBehalfPayment] = useState(false),
    [onBehalfCharges, setOnBehalfCharges] = useState([]),
    [onBehalfEvidence, setOnBehalfEvidence] = useState(null),
    [onBehalfPayment, setOnBehalfPayment] = useState({
      propertyId: '',
      chargeId: '',
      transactionAccountId: '',
      paymentMode: 'CASH',
      amount: '',
      transactionDate: today(),
      transactionReference: '',
      bankName: '',
      chequeNumber: '',
      chequeDate: '',
      cashVoucherNumber: '',
      payerName: '',
      remarks: '',
    }),
    [page, setPage] = useState(0),
    [evidence, setEvidence] = useState(null),
    [paymentHistories, setPaymentHistories] = useState({}),
    [campaign, setCampaign] = useState(blankCampaign),
    [editingCampaignId, setEditingCampaignId] = useState(null),
    [entry, setEntry] = useState({
      entryType: 'EXPENSE',
      accountingUnitId: '',
      fundId: '',
      transactionAccountId: '',
      category: 'GENERAL',
      amount: '',
      transactionDate: today(),
      title: '',
      description: '',
      payerOrPayee: '',
      paymentMode: 'BANK_TRANSFER',
      transactionReference: '',
      chequeNumber: '',
      cashVoucherNumber: '',
    }),
    [entryEvidence, setEntryEvidence] = useState(null),
    [payingEntry, setPayingEntry] = useState(null),
    [expensePayment, setExpensePayment] = useState({
      transactionAccountId: '',
      paymentMode: 'BANK_TRANSFER',
      paymentDate: today(),
      transactionReference: '',
      chequeNumber: '',
      cashVoucherNumber: '',
      remarks: '',
    }),
    [expensePaymentEvidence, setExpensePaymentEvidence] = useState(null),
    [cashbookHistories, setCashbookHistories] = useState({}),
    [payment, setPayment] = useState({
      campaignId: '',
      chargeId: '',
      propertyId: '',
      transactionAccountId: '',
      paymentMode: 'UPI',
      amount: '',
      transactionDate: today(),
      transactionReference: '',
      payerName: session?.name || '',
      bankName: '',
      chequeNumber: '',
      chequeDate: '',
      cashVoucherNumber: '',
      remarks: '',
    }),
    [communities, setCommunities] = useState([]),
    [selectedCommunityId, setSelectedCommunityId] = useState(
      session?.communityId ? String(session.communityId) : ''
    ),
    [reportType, setReportType] = useState('collections'),
    [reportFilters, setReportFilters] = useState({
      from: '',
      to: '',
      search: '',
      sortBy: '',
      direction: 'asc',
      page: 0,
      size: 10,
    }),
    [reportView, setReportView] = useState({
      title: '',
      headers: [],
      content: [],
      totalElements: 0,
      totalPages: 0,
    }),
    [reportLoading, setReportLoading] = useState(false);
  const communityId = superAdmin
    ? selectedCommunityId
      ? Number(selectedCommunityId)
      : null
    : session?.communityId;
  const selectedCommunity = communities.find(
    (community) => String(community.id) === String(communityId)
  );
  const communityName =
    selectedCommunity?.displayName ||
    selectedCommunity?.name ||
    session?.communityName ||
    'your community';
  const params = communityId ? { communityId } : {};
  useEffect(() => {
    if (!superAdmin) return;
    get('/api/communities')
      .then((items) => {
        const active = (items || []).filter(
          (community) => community.status === 'ACTIVE'
        );
        setCommunities(active);
        setSelectedCommunityId((current) =>
          current ? current : active[0]?.id ? String(active[0].id) : ''
        );
      })
      .catch((e) => setError(e.message));
  }, [get, superAdmin]);
  const loadFoundation = useCallback(
    () => get('/api/finance/foundation', { params }).then(setFoundation),
    [get, communityId]
  );
  const load = useCallback(async () => {
    if (admin && !communityId) return;
    try {
      setError('');
      if (admin) {
        const [f, s, c, q] = await Promise.all([
          get('/api/finance/foundation', { params }),
          get('/api/finance/summary', { params }),
          get('/api/finance/campaigns', {
            params: {
              ...params,
              ...campaignFilters,
              search: campaignFilters.search || undefined,
              from: campaignFilters.from || undefined,
              to: campaignFilters.to || undefined,
              page: campaignPage,
              size: campaignFilters.size,
            },
          }),
          get('/api/finance/payment-submissions/verification-queue', {
            params: {
              ...params,
              ...queueFilters,
              search: queueFilters.search || undefined,
              from: queueFilters.from || undefined,
              to: queueFilters.to || undefined,
              page,
              size: queueFilters.size,
            },
          }),
        ]);
        setFoundation(f);
        setSummary(s);
        setCampaigns(c);
        setQueue(q);
        try {
          const structure = await get('/api/community-structure', { params });
          setStructureUnits(structure.units || []);
          setProperties(structure.properties || []);
        } catch {
          setStructureUnits([]);
          setProperties([]);
        }
        if (tab === 'cashbook')
          setCashbook(
            await get('/api/finance/cashbook', {
              params: {
                ...params,
                ...cashbookFilters,
                search: cashbookFilters.search || undefined,
                from: cashbookFilters.from || undefined,
                to: cashbookFilters.to || undefined,
                page: cashbookPage,
                size: cashbookFilters.size,
              },
            })
          );
        if (resident) {
          const [d, available, history] = await Promise.all([
            get('/api/finance/my-charges', { params: { page, size: 10 } }),
            get('/api/finance/my-campaigns', {
              params: { page: 0, size: 100 },
            }),
            get('/api/finance/my-payment-submissions', {
              params: {
                ...paymentHistoryFilters,
                search: paymentHistoryFilters.search || undefined,
                from: paymentHistoryFilters.from || undefined,
                to: paymentHistoryFilters.to || undefined,
                page: paymentHistoryPage,
                size: paymentHistoryFilters.size,
              },
            }),
          ]);
          setDues(d);
          setResidentCampaigns(available);
          setPaymentHistory(history);
        }
      } else {
        const [d, available, history] = await Promise.all([
          get('/api/finance/my-charges', {
            params: { page, size: 10 },
          }),
          get('/api/finance/my-campaigns', {
            params: { page: 0, size: 100 },
          }),
          get('/api/finance/my-payment-submissions', {
            params: {
              ...paymentHistoryFilters,
              search: paymentHistoryFilters.search || undefined,
              from: paymentHistoryFilters.from || undefined,
              to: paymentHistoryFilters.to || undefined,
              page: paymentHistoryPage,
              size: paymentHistoryFilters.size,
            },
          }),
        ]);
        setDues(d);
        setResidentCampaigns(available);
        setPaymentHistory(history);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [admin, resident, get, communityId, page, tab, queueFilters, campaignPage, campaignFilters, paymentHistoryPage, paymentHistoryFilters, cashbookPage, cashbookFilters]);
  useEffect(() => {
    load();
  }, [load]);
  const loadReport = useCallback(async () => {
    if (tab !== 'reports' || !communityId) return;
    setReportLoading(true);
    try {
      const view = await get(`/api/finance/reports/${reportType}/view`, {
        params: {
          ...params,
          ...reportFilters,
          from: reportFilters.from || undefined,
          to: reportFilters.to || undefined,
          search: reportFilters.search || undefined,
          sortBy: reportFilters.sortBy || undefined,
        },
      });
      setReportView(view);
      setError('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReportLoading(false);
    }
  }, [tab, communityId, get, reportType, reportFilters]);
  useEffect(() => {
    loadReport();
  }, [loadReport]);
  useEffect(() => setPage(0), [tab]);
  useEffect(() => {
    setPage(0);
    setMessage('');
    setError('');
  }, [communityId]);
  const funds = useMemo(
    () =>
      foundation.funds.filter(
        (f) =>
          !campaign.accountingUnitId ||
          String(f.accounting_unit_id) === String(campaign.accountingUnitId)
      ),
    [foundation, campaign.accountingUnitId]
  );
  const accounts =
    resident && tab === 'pay'
      ? residentAccounts
      : admin
        ? foundation.transactionAccounts
        : residentAccounts;
  const selectedPaymentAccount = accounts.find(
    (item) => String(item.id) === String(payment.transactionAccountId)
  );
  useEffect(() => {
    if (!resident || !payment.campaignId) {
      setResidentAccounts([]);
      return;
    }
    get(`/api/finance/payment-instructions/${payment.campaignId}`)
      .then(setResidentAccounts)
      .catch((e) => setError(e.message));
  }, [resident, payment.campaignId, get]);
  const saveCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...campaign,
        communityId,
        accountingUnitId: Number(campaign.accountingUnitId),
        fundId: Number(campaign.fundId),
        amountPerUnit: campaign.amountPerUnit
          ? Number(campaign.amountPerUnit)
          : null,
        minimumContribution: campaign.minimumContribution
          ? Number(campaign.minimumContribution)
          : null,
        maximumContribution: campaign.maximumContribution
          ? Number(campaign.maximumContribution)
          : null,
        structureUnitIds: campaign.structureUnitIds.map(Number),
        propertyIds: campaign.propertyIds.map(Number),
        dueDate: campaign.dueDate || null,
      };
      if (editingCampaignId)
        await put(`/api/finance/campaigns/${editingCampaignId}`, payload);
      else await post('/api/finance/campaigns', payload);
      setCampaign(blankCampaign);
      setEditingCampaignId(null);
      setMessage(
        editingCampaignId
          ? 'Collection campaign updated.'
          : 'Collection campaign created.'
      );
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const editCampaign = async (item) => {
    try {
      const detail = await get(`/api/finance/campaigns/${item.id}`, { params });
      setEditingCampaignId(item.id);
      setCampaign({
        title: detail.title || '',
        description: detail.description || '',
        collectionType: detail.collection_type,
        collectionMode: detail.collection_mode,
        billingScope: detail.billing_scope || 'COMMUNITY',
        contributionType: detail.contribution_type || 'COMPULSORY',
        structureUnitIds: detail.structureUnitIds || [],
        propertyIds: detail.propertyIds || [],
        startDate: detail.start_date,
        dueDate: detail.due_date || '',
        amountPerUnit: detail.amount_per_unit || '',
        minimumContribution: detail.minimum_contribution || '',
        maximumContribution: detail.maximum_contribution || '',
        status: detail.status,
        accountingUnitId: String(detail.accounting_unit_id),
        fundId: String(detail.fund_id),
      });
    } catch (x) {
      setError(x.message);
    }
  };
  const changeCampaignStatus = async (item, status) => {
    const reason =
      status === 'CANCELLED'
        ? window.prompt('Reason for cancelling this collection')
        : '';
    if (status === 'CANCELLED' && !reason) return;
    try {
      await put(`/api/finance/campaigns/${item.id}/status`, { status, reason });
      setMessage(`Collection ${status.toLowerCase()}.`);
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const deleteCampaign = async (item) => {
    if (!window.confirm(`Delete draft collection "${item.title}"?`)) return;
    try {
      await remove(`/api/finance/campaigns/${item.id}`, { params });
      setMessage('Draft collection deleted.');
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const generateCharges = async (selectedCampaign) => {
    try {
      const targets = await get(
        `/api/finance/campaigns/${selectedCampaign.id}/targets`,
        { params }
      );
      setChargePreview({ campaign: selectedCampaign, targets });
    } catch (x) {
      setError(x.message);
    }
  };
  const confirmChargeGeneration = async (propertyIds, customAmounts) => {
    if (!chargePreview) return;
    const selectedCampaign = chargePreview.campaign;
    if (!propertyIds.length && chargePreview.campaign.contribution_type === 'VOLUNTARY') {
      setError('Select at least one eligible property before generating charges.');
      return;
    }
    try {
      const result = selectedCampaign.contribution_type === 'VOLUNTARY'
        ? await post(
            `/api/finance/campaigns/${selectedCampaign.id}/generate-charges`,
            { propertyIds, customAmounts }
          )
        : await put(
            `/api/finance/campaigns/${selectedCampaign.id}/charge-assignments`,
            { propertyIds, customAmounts }
          );
      setChargePreview(null);
      setMessage(
        selectedCampaign.contribution_type === 'VOLUNTARY'
          ? 'Voluntary contribution published to selected residents.'
          : `Charge assignments saved: ${result.generated || 0} created, ${result.reactivated || 0} restored, ${result.updated || 0} updated and ${result.cancelled || 0} removed.`
      );
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const createNextCycle = async (item) => {
    const startDate = window.prompt(
      'Next billing-cycle start date (YYYY-MM-DD)',
      item.due_date || new Date().toISOString().slice(0, 10)
    );
    if (!startDate) return;
    const dueDate = window.prompt(
      'Next billing-cycle due date (YYYY-MM-DD, optional)',
      ''
    );
    try {
      await post(`/api/finance/campaigns/${item.id}/next-cycle`, {
        startDate,
        dueDate: dueDate || null,
        title: `${item.title} - ${startDate}`,
      });
      setMessage('Next billing cycle created as a draft. Review and generate its charges.');
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const submitPayment = async (e) => {
    e.preventDefault();
    setError('');
    let submitted = null;
    try {
      submitted = await post('/api/finance/payment-submissions', {
        ...payment,
        campaignId: Number(payment.campaignId),
        chargeId: payment.chargeId ? Number(payment.chargeId) : null,
        propertyId: Number(payment.propertyId),
        transactionAccountId: Number(payment.transactionAccountId),
        amount: Number(payment.amount),
        chequeDate: payment.chequeDate || null,
      });
      if (evidence) {
        const form = new FormData();
        form.append('file', evidence);
        await post(
          `/api/finance/payment-submissions/${submitted.id}/evidence`,
          form
        );
      }
      setEvidence(null);
      setMessage('Payment details submitted for verification.');
      load();
    } catch (x) {
      if (submitted) {
        setEvidence(null);
        setMessage(
          'Payment details were submitted, but the proof could not be uploaded. Open Payment history to review the submission.'
        );
        setError(x.message);
        load();
      } else {
        setError(x.message);
      }
    }
  };
  const loadPaymentHistory = async (id) => {
    try {
      const items = await get(`/api/finance/payment-submissions/${id}/history`);
      setPaymentHistories((current) => ({ ...current, [id]: items }));
    } catch (x) {
      setError(x.message);
    }
  };
  const review = async (id, action) => {
    const remarks =
      window.prompt(
        action === 'APPROVE'
          ? 'Optional verification remarks'
          : 'Reason / correction required'
      ) || '';
    try {
      await put(`/api/finance/payment-submissions/${id}/review`, {
        action,
        remarks,
        allocations: [],
      });
      setMessage(`Payment ${action.toLowerCase()}.`);
      load();
      return true;
    } catch (x) {
      setError(x.message);
      return false;
    }
  };
  const selectOnBehalfProperty = async (propertyId) => {
    setOnBehalfPayment((current) => ({
      ...current,
      propertyId,
      chargeId: '',
      transactionAccountId: '',
      amount: '',
    }));
    if (!propertyId) {
      setOnBehalfCharges([]);
      return;
    }
    try {
      setOnBehalfCharges(
        await get(`/api/finance/properties/${propertyId}/open-charges`, {
          params,
        })
      );
    } catch (x) {
      setError(x.message);
    }
  };
  const selectOnBehalfCharge = (chargeId) => {
    const charge = onBehalfCharges.find((item) => String(item.id) === String(chargeId));
    setOnBehalfPayment((current) => ({
      ...current,
      chargeId,
      transactionAccountId: '',
      amount: charge?.outstanding_amount || '',
    }));
  };
  const saveOnBehalfPayment = async (event) => {
    event.preventDefault();
    const charge = onBehalfCharges.find(
      (item) => String(item.id) === String(onBehalfPayment.chargeId)
    );
    try {
      const result = await post('/api/finance/payments-on-behalf', {
        ...onBehalfPayment,
        communityId,
        campaignId: Number(charge.collection_campaign_id),
        chargeId: Number(onBehalfPayment.chargeId),
        transactionAccountId: Number(onBehalfPayment.transactionAccountId),
        amount: Number(onBehalfPayment.amount),
      });
      if (onBehalfEvidence && result?.submissionId) {
        const form = new FormData();
        form.append('file', onBehalfEvidence);
        await api.post(
          `/api/finance/payment-submissions/${result.submissionId}/evidence`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      setShowOnBehalfPayment(false);
      setOnBehalfCharges([]);
      setOnBehalfEvidence(null);
      setMessage('Resident payment recorded and approved.');
      load();
    } catch (x) {
      setError(x.response?.data?.message || x.message);
    }
  };
  const saveEntry = async (event) => {
    event.preventDefault();
    try {
      const result = await post('/api/finance/cashbook', {
        ...entry,
        communityId,
        accountingUnitId: Number(entry.accountingUnitId),
        fundId: Number(entry.fundId),
        transactionAccountId:
          entry.entryType === 'INCOME'
            ? Number(entry.transactionAccountId)
            : null,
        amount: Number(entry.amount),
      });
      if (entryEvidence && result?.id) {
        const form = new FormData();
        form.append('file', entryEvidence);
        await api.post(
          `/api/finance/cashbook/${result.id}/evidence?kind=SOURCE`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      setEntry({
        ...entry,
        amount: '',
        title: '',
        description: '',
        payerOrPayee: '',
        transactionReference: '',
        chequeNumber: '',
        cashVoucherNumber: '',
      });
      setEntryEvidence(null);
      setMessage('Cashbook entry recorded.');
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const decideEntry = async (id, action) => {
    const remarks =
      window.prompt(
        action === 'REJECT'
          ? 'Enter the reason for rejection'
          : 'Optional approval remarks'
      ) || '';
    if (action === 'REJECT' && !remarks.trim()) return;
    try {
      await put(`/api/finance/cashbook/${id}/decision`, {
        action,
        remarks,
      });
      setMessage(`Entry ${action.toLowerCase()}.`);
      load();
    } catch (x) {
      setError(x.message);
    }
  };
  const openExpensePayment = (item) => {
    setPayingEntry(item);
    setExpensePayment({
      transactionAccountId: '',
      paymentMode: 'BANK_TRANSFER',
      paymentDate: today(),
      transactionReference: '',
      chequeNumber: '',
      cashVoucherNumber: '',
      remarks: '',
    });
    setExpensePaymentEvidence(null);
  };
  const completeExpensePayment = async (event) => {
    event.preventDefault();
    try {
      await put(`/api/finance/cashbook/${payingEntry.id}/payment`, {
        ...expensePayment,
        transactionAccountId: Number(expensePayment.transactionAccountId),
      });
      if (expensePaymentEvidence) {
        const form = new FormData();
        form.append('file', expensePaymentEvidence);
        await api.post(
          `/api/finance/cashbook/${payingEntry.id}/evidence?kind=PAYMENT`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }
      setPayingEntry(null);
      setExpensePaymentEvidence(null);
      setMessage('Expense payment recorded.');
      load();
    } catch (x) {
      setError(x.response?.data?.message || x.message);
    }
  };
  const openCashbookEvidence = async (id, kind) => {
    try {
      const response = await api.get(
        `/api/finance/cashbook/${id}/evidence?kind=${kind}`,
        { responseType: 'blob' }
      );
      window.open(URL.createObjectURL(response.data), '_blank', 'noopener');
    } catch (x) {
      setError(x.response?.data?.message || 'Unable to open evidence.');
    }
  };
  const showCashbookHistory = async (item) => {
    setSelectedCashbookEntry(item);
    if (cashbookHistories[item.id]) return;
    try {
      const history = await get(`/api/finance/cashbook/${item.id}/history`);
      setCashbookHistories((current) => ({ ...current, [item.id]: history }));
    } catch (x) {
      setError(x.message);
    }
  };
  const download = async (type, format) => {
    try {
      const response = await api.get(`/api/finance/reports/${type}.${format}`, {
        params: {
          ...params,
          from: reportFilters.from || undefined,
          to: reportFilters.to || undefined,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data),
        link = document.createElement('a');
      link.href = url;
      link.download = `community-finance-${type}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (x) {
      setError(x.response?.data?.message || 'Unable to export finance report.');
    }
  };
  const downloadReceipt = async (receipt) => {
    try {
      const response = await api.get(`/api/finance/receipts/${receipt.id}.pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${receipt.receipt_number || 'community-receipt'}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (x) {
      setError(x.response?.data?.message || 'Unable to download receipt.');
    }
  };
  const viewPaymentEvidence = async (submission) => {
    try {
      const response = await api.get(
        `/api/finance/payment-submissions/${submission.id}/evidence`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (x) {
      setError(x.response?.data?.message || 'Payment evidence is not available.');
    }
  };
  const payableCampaigns = useMemo(
    () =>
      (residentCampaigns.content || []).filter(
        (item) =>
          item.contribution_type === 'VOLUNTARY' ||
          (item.charge_id && Number(item.outstanding_amount) > 0)
      ),
    [residentCampaigns.content]
  );
  const canSubmitPayment = payableCampaigns.length > 0;
  useEffect(() => {
    if (resident && tab === 'pay' && !canSubmitPayment) {
      setTab(admin ? 'overview' : 'dues');
    }
  }, [admin, resident, tab, canSubmitPayment]);
  const tabs = [
    ...(admin
      ? [
        ['overview', 'Overview'],
        ['campaigns', 'Collections'],
        ['verification', 'Payment verification'],
        ['cashbook', 'Income & expenses'],
        ['setup', 'Funds & accounts'],
        ['reports', 'Finance reports'],
      ]
      : []),
    ...(resident
      ? [
        ['dues', 'My dues & contributions'],
        ...(canSubmitPayment ? [['pay', 'Submit payment']] : []),
        ['payment-history', 'Payment history'],
      ]
      : []),
  ];
  return (
    <div className="finance-page">
      <div className="finance-heading">
        <div>
          <span>Community finance</span>
          <h2>Collections, income &amp; expenses</h2>
          <p>
            Financial activity for{' '}
            <CommunityIdentity
              community={selectedCommunity || session?.community || { name: communityName, displayName: communityName }}
              size="xs"
              compact
            />. Community money moves directly to configured community accounts;
            MyCommunity only records and verifies activity.
          </p>
        </div>
      </div>
      {superAdmin && (
        <section className="finance-community-scope">
          <label>
            <FinanceFieldLabel label="Community" />
            <select
              className="form-select"
              value={selectedCommunityId}
              onChange={(event) => setSelectedCommunityId(event.target.value)}
            >
              <option value="">Select a community</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.displayName || community.name}
                  {community.city ? ` · ${community.city}` : ''}
                </option>
              ))}
            </select>
          </label>
          <small>
            Every total, collection, account and export below is restricted to
            the selected community.
          </small>
        </section>
      )}
      {error && (
        <Alert type="danger" message={error} onClose={() => setError('')} />
      )}{' '}
      {message && (
        <Alert
          type="success"
          message={message}
          onClose={() => setMessage('')}
        />
      )}
      {(!superAdmin || communityId) && <nav className="finance-tabs">
        {tabs.map(([id, label]) => (
          <button
            type="button"
            key={id}
            className={tab === id ? 'active' : ''}
            onClick={(event) => {
              setTab(id);
              event.currentTarget.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
              });
            }}
          >
            {label}
          </button>
        ))}
      </nav>}
      {tab === 'overview' && (
        <>
          <div className="finance-metrics">
            <Metric label="Billed" value={money(summary.collections?.billed)} metricKey="billed" breakdown={summary.breakdown} currency />
            <Metric
              label="Collected"
              value={money(summary.collections?.collected)}
              metricKey="collected"
              breakdown={summary.breakdown}
              currency
            />
            <Metric
              label="Outstanding"
              value={money(summary.collections?.outstanding)}
              metricKey="outstanding"
              breakdown={summary.breakdown}
              currency
            />
            <Metric
              label="Pending verification"
              value={summary.pendingVerification || 0}
              metricKey="pending_verification"
              breakdown={summary.breakdown}
            />
            <Metric label="Income" value={money(summary.cashbook?.income)} metricKey="income" breakdown={summary.breakdown} currency />
            <Metric
              label="Expenses"
              value={money(summary.cashbook?.expenses)}
              metricKey="expenses"
              breakdown={summary.breakdown}
              currency
            />
          </div>
          <Section title="Recent collections">
            <CampaignTable items={campaigns.content} />
          </Section>
        </>
      )}
      {tab === 'reports' && (
        <Section title="Finance reports">
          <div className="finance-report-toolbar">
            <label>
              Report
              <select
                value={reportType}
                onChange={(event) => {
                  setReportType(event.target.value);
                  setReportFilters((current) => ({
                    ...current,
                    page: 0,
                    sortBy: '',
                  }));
                }}
              >
                {financeReportTypes.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              From
              <input
                type="date"
                value={reportFilters.from}
                onChange={(event) =>
                  setReportFilters({
                    ...reportFilters,
                    from: event.target.value,
                    page: 0,
                  })
                }
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={reportFilters.to}
                onChange={(event) =>
                  setReportFilters({
                    ...reportFilters,
                    to: event.target.value,
                    page: 0,
                  })
                }
              />
            </label>
            <label>
              Search
              <input
                placeholder="Search visible report fields"
                value={reportFilters.search}
                onChange={(event) =>
                  setReportFilters({
                    ...reportFilters,
                    search: event.target.value,
                    page: 0,
                  })
                }
              />
            </label>
            <label>
              Rows
              <select
                value={reportFilters.size}
                onChange={(event) =>
                  setReportFilters({
                    ...reportFilters,
                    size: Number(event.target.value),
                    page: 0,
                  })
                }
              >
                {[5, 10, 25, 50, 100].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </label>
            <div className="finance-actions">
              <button type="button" onClick={() => download(reportType, 'csv')}>
                Export CSV
              </button>
              <button type="button" onClick={() => download(reportType, 'pdf')}>
                Export PDF
              </button>
            </div>
          </div>
          <div className="table-responsive finance-report-table finance-mobile-cards">
            <table className="table">
              <thead>
                <tr>
                  {reportView.headers?.map((header) => (
                    <th key={header}>
                      <button
                        type="button"
                        className="finance-sort-button"
                        onClick={() =>
                          setReportFilters((current) => ({
                            ...current,
                            page: 0,
                            sortBy: header,
                            direction:
                              current.sortBy === header &&
                              current.direction === 'asc'
                                ? 'desc'
                                : 'asc',
                          }))
                        }
                      >
                        {header}{' '}
                        {reportFilters.sortBy === header
                          ? reportFilters.direction === 'asc'
                            ? '↑'
                            : '↓'
                          : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportView.content?.map((row, rowIndex) => (
                  <tr key={`${reportFilters.page}-${rowIndex}`}>
                    {row.map((value, columnIndex) => (
                      <td
                        key={`${rowIndex}-${columnIndex}`}
                        data-label={reportView.headers?.[columnIndex] || 'Value'}
                      >
                        {value || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {!reportLoading && !reportView.content?.length && (
                  <tr>
                    <td
                      colSpan={Math.max(reportView.headers?.length || 1, 1)}
                      className="text-center"
                    >
                      No finance records match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {reportLoading && <p className="finance-empty">Loading report…</p>}
          <div className="finance-report-pagination">
            <small>
              {reportView.totalElements || 0} record
              {reportView.totalElements === 1 ? '' : 's'}
            </small>
            <div>
              <button
                type="button"
                disabled={reportFilters.page === 0}
                onClick={() =>
                  setReportFilters({
                    ...reportFilters,
                    page: reportFilters.page - 1,
                  })
                }
              >
                Previous
              </button>
              <span>
                Page {reportFilters.page + 1} of{' '}
                {Math.max(reportView.totalPages || 1, 1)}
              </span>
              <button
                type="button"
                disabled={
                  reportFilters.page + 1 >= (reportView.totalPages || 0)
                }
                onClick={() =>
                  setReportFilters({
                    ...reportFilters,
                    page: reportFilters.page + 1,
                  })
                }
              >
                Next
              </button>
            </div>
          </div>
        </Section>
      )}
      {tab === 'campaigns' && (
        chargePreview ? (
          <ChargeGenerationPreview
            preview={chargePreview}
            onCancel={() => setChargePreview(null)}
            onGenerate={confirmChargeGeneration}
          />
        ) : (
        <div className="finance-split">
          <Section title="Create collection">
            <form className="finance-form" onSubmit={saveCampaign}>
              <Field label="Title">
                <input
                  required
                  value={campaign.title}
                  onChange={(e) =>
                    setCampaign({ ...campaign, title: e.target.value })
                  }
                />
              </Field>
              <Field label="Description">
                <textarea
                  value={campaign.description}
                  onChange={(e) =>
                    setCampaign({ ...campaign, description: e.target.value })
                  }
                />
              </Field>
              <div className="finance-two">
                <Field label="Type">
                  <select
                    value={campaign.collectionType}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        collectionType: e.target.value,
                      })
                    }
                  >
                    {[
                      'MAINTENANCE',
                      'EVENT',
                      'EMERGENCY_FUND',
                      'REPAIR',
                      'FESTIVAL',
                      'DONATION',
                      'AMENITY',
                      'OTHER',
                    ].map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Mode">
                  <select
                    value={campaign.collectionMode}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        collectionMode: e.target.value,
                      })
                    }
                  >
                    {[ 
                      'FIXED_PER_UNIT',
                      'CUSTOM_PER_UNIT',
                      'COMMON_AMOUNT',
                      'MANUAL_ASSIGNMENT',
                    ].map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="finance-two">
                <Field label="Contribution requirement">
                  <select
                    value={campaign.contributionType}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        contributionType: e.target.value,
                      })
                    }
                  >
                    <option value="COMPULSORY">Compulsory bill / due</option>
                    <option value="VOLUNTARY">Voluntary contribution</option>
                  </select>
                </Field>
                <Field label="Who should receive it?">
                  <select
                    value={campaign.billingScope}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        billingScope: e.target.value,
                        structureUnitIds: [],
                        propertyIds: [],
                      })
                    }
                  >
                    <option value="COMMUNITY">Entire community</option>
                    <option value="DIVISIONS">
                      Selected divisions / wings
                    </option>
                    <option value="PROPERTIES">Selected homes</option>
                  </select>
                </Field>
              </div>
              {campaign.billingScope === 'DIVISIONS' && (
                <fieldset className="finance-targets">
                  <legend>
                    <FinanceFieldLabel
                      label="Select divisions, wings or blocks"
                      help="Select every address group whose active properties should receive this collection. Charges are generated for eligible homes inside the selected groups."
                    />
                  </legend>
                  {structureUnits
                    .filter((item) => item.active)
                    .map((item) => (
                      <label key={item.id}>
                        <input
                          type="checkbox"
                          checked={campaign.structureUnitIds.includes(item.id)}
                          onChange={(event) =>
                            setCampaign({
                              ...campaign,
                              structureUnitIds: event.target.checked
                                ? [...campaign.structureUnitIds, item.id]
                                : campaign.structureUnitIds.filter(
                                    (id) => id !== item.id
                                  ),
                            })
                          }
                        />
                        {item.name} ({item.code})
                      </label>
                    ))}
                </fieldset>
              )}
              {campaign.billingScope === 'PROPERTIES' && (
                <Field label="Select homes">
                  <select
                    multiple
                    size="6"
                    value={campaign.propertyIds.map(String)}
                    onChange={(event) =>
                      setCampaign({
                        ...campaign,
                        propertyIds: Array.from(
                          event.target.selectedOptions,
                          (option) => Number(option.value)
                        ),
                      })
                    }
                  >
                    {properties
                      .filter((item) => item.active)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.display_name}
                        </option>
                      ))}
                  </select>
                </Field>
              )}
              <Field label="Accounting unit">
                <select
                  required
                  value={campaign.accountingUnitId}
                  onChange={(e) =>
                    setCampaign({
                      ...campaign,
                      accountingUnitId: e.target.value,
                      fundId: '',
                    })
                  }
                >
                  <option value="">Select</option>
                  {foundation.accountingUnits
                    .filter((x) => x.active)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Fund">
                <select
                  required
                  value={campaign.fundId}
                  onChange={(e) =>
                    setCampaign({ ...campaign, fundId: e.target.value })
                  }
                >
                  <option value="">Select</option>
                  {funds
                    .filter((x) => x.active)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </Field>
              <div className="finance-two">
                <Field label="Start">
                  <input
                    type="date"
                    value={campaign.startDate}
                    onChange={(e) =>
                      setCampaign({ ...campaign, startDate: e.target.value })
                    }
                  />
                </Field>
                <Field label="Due">
                  <input
                    type="date"
                    value={campaign.dueDate}
                    onChange={(e) =>
                      setCampaign({ ...campaign, dueDate: e.target.value })
                    }
                  />
                </Field>
              </div>
              {campaign.contributionType === 'COMPULSORY' ? (
                <Field label="Amount billed to each property">
                  <input
                    required={!['CUSTOM_PER_UNIT', 'MANUAL_ASSIGNMENT'].includes(campaign.collectionMode)}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={campaign.amountPerUnit}
                    onChange={(e) =>
                      setCampaign({
                        ...campaign,
                        amountPerUnit: e.target.value,
                      })
                    }
                  />
                </Field>
              ) : (
                <div className="finance-two">
                  <Field label="Minimum contribution (optional)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={campaign.minimumContribution}
                      onChange={(e) =>
                        setCampaign({
                          ...campaign,
                          minimumContribution: e.target.value,
                        })
                      }
                    />
                  </Field>
                  <Field label="Maximum contribution (optional)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={campaign.maximumContribution}
                      onChange={(e) =>
                        setCampaign({
                          ...campaign,
                          maximumContribution: e.target.value,
                        })
                      }
                    />
                  </Field>
                </div>
              )}
              <div className="finance-actions">
                <button className="btn btn-primary">
                  {editingCampaignId ? 'Update draft collection' : 'Create campaign'}
                </button>
                {editingCampaignId && (
                  <button type="button" onClick={() => { setEditingCampaignId(null); setCampaign(blankCampaign); }}>
                    Cancel edit
                  </button>
                )}
              </div>
            </form>
          </Section>
          <Section title="Collection campaigns">
            <div className="finance-filter-grid finance-collection-filters">
              <Field label="Status">
                <select
                  value={campaignFilters.status}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      status: event.target.value,
                    });
                  }}
                >
                  <option value="ALL">All statuses</option>
                  {['DRAFT', 'ACTIVE', 'CLOSED', 'CANCELLED'].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <Field label="Search">
                <input
                  value={campaignFilters.search}
                  placeholder="Title, description, fund or accounting area"
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      search: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Collection type">
                <select
                  value={campaignFilters.collectionType}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      collectionType: event.target.value,
                    });
                  }}
                >
                  <option value="ALL">All types</option>
                  {[
                    'MAINTENANCE',
                    'EVENT',
                    'EMERGENCY_FUND',
                    'REPAIR',
                    'FESTIVAL',
                    'DONATION',
                    'AMENITY',
                    'OTHER',
                  ].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <Field label="Requirement">
                <select
                  value={campaignFilters.contributionType}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      contributionType: event.target.value,
                    });
                  }}
                >
                  <option value="ALL">All requirements</option>
                  <option value="COMPULSORY">Compulsory</option>
                  <option value="VOLUNTARY">Voluntary</option>
                </select>
              </Field>
              <Field label="Starts from">
                <input
                  type="date"
                  value={campaignFilters.from}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      from: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Starts to">
                <input
                  type="date"
                  value={campaignFilters.to}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      to: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Rows per page">
                <select
                  value={campaignFilters.size}
                  onChange={(event) => {
                    setCampaignPage(0);
                    setCampaignFilters({
                      ...campaignFilters,
                      size: Number(event.target.value),
                    });
                  }}
                >
                  {[5, 10, 20, 50].map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </select>
              </Field>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setCampaignPage(0);
                  setCampaignFilters({
                    status: 'ALL',
                    search: '',
                    collectionType: 'ALL',
                    contributionType: 'ALL',
                    from: '',
                    to: '',
                    size: 5,
                  });
                }}
              >
                Clear filters
              </button>
            </div>
            <CampaignTable
              items={campaigns.content}
              onGenerate={generateCharges}
              onEdit={editCampaign}
              onStatus={changeCampaignStatus}
              onDelete={deleteCampaign}
              onNextCycle={createNextCycle}
            />
            {!campaigns.content?.length && (
              <p className="finance-empty">
                No collection campaigns match the selected filters.
              </p>
            )}
            <Pager
              data={campaigns}
              page={campaignPage}
              setPage={setCampaignPage}
            />
          </Section>
        </div>
        )
      )}
      {tab === 'verification' && (
        <Section title="Payment verification">
          {selectedVerification ? (
            <PaymentVerificationDetail
              item={selectedVerification}
              history={paymentHistories[selectedVerification.id] || []}
              onBack={() => setSelectedVerification(null)}
              onLoadHistory={() => loadPaymentHistory(selectedVerification.id)}
              onReview={async (...args) => {
                if (await review(...args)) setSelectedVerification(null);
              }}
              onEvidence={viewPaymentEvidence}
            />
          ) : (
            <>
              <div className="finance-section-toolbar">
                <div>
                  <strong>Resident payment operations</strong>
                  <span>Verify resident submissions or record a payment received by the office.</span>
                </div>
                <button className="btn btn-primary" type="button" onClick={() => setShowOnBehalfPayment(true)}>
                  Record payment on behalf
                </button>
              </div>
              <div className="finance-verification-filters">
                <Field label="Status">
                  <select value={queueFilters.status} onChange={(event) => { setPage(0); setQueueFilters({ ...queueFilters, status: event.target.value }); }}>
                    <option value="PENDING">Pending action</option>
                    <option value="ALL">All payment records</option>
                    <option value="CORRECTION_REQUESTED">Correction requested</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="UNDER_VERIFICATION">Under verification</option>
                  </select>
                </Field>
                <Field label="Search" help="Search resident, email, division, property, collection or transaction reference.">
                  <input value={queueFilters.search} placeholder="Resident, unit, collection or reference" onChange={(event) => { setPage(0); setQueueFilters({ ...queueFilters, search: event.target.value }); }} />
                </Field>
                <Field label="Submitted from"><input type="date" value={queueFilters.from} onChange={(event) => { setPage(0); setQueueFilters({ ...queueFilters, from: event.target.value }); }} /></Field>
                <Field label="Submitted to"><input type="date" value={queueFilters.to} onChange={(event) => { setPage(0); setQueueFilters({ ...queueFilters, to: event.target.value }); }} /></Field>
                <Field label="Rows per page">
                  <select value={queueFilters.size} onChange={(event) => { setPage(0); setQueueFilters({ ...queueFilters, size: Number(event.target.value) }); }}>
                    {[5, 10, 20, 50].map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </Field>
              </div>
              <p className="finance-help">{queue.totalElements || 0} payment record(s). Open Details for proof, remarks, actions and the complete version history.</p>
              <div className="table-responsive finance-verification-register finance-mobile-cards">
                <table className="table">
                  <thead><tr><th>Submitted</th><th>Resident</th><th>Home</th><th>Collection</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {queue.content?.map((x) => (
                      <tr key={x.id}>
                        <td data-label="Submitted">{dateTime(x.submitted_at)}</td>
                        <td data-label="Resident"><strong>{x.resident_name}</strong><small>{x.resident_email || x.resident_mobile}</small></td>
                        <td data-label="Home">{x.division_code || x.division_name || 'Root'} · {x.property_name || x.property_number}</td>
                        <td data-label="Collection">{x.campaign_title}</td>
                        <td data-label="Amount">{money(x.amount_submitted)}</td>
                        <td data-label="Status"><span className="finance-status">{x.verification_status}</span></td>
                        <td data-label="Action"><button className="finance-details-button" type="button" onClick={() => setSelectedVerification(x)}>View details</button></td>
                      </tr>
                    ))}
                    {!queue.content?.length && <tr><td colSpan="7">No payment records match these filters.</td></tr>}
                  </tbody>
                </table>
              </div>
              <Pager data={queue} page={page} setPage={setPage} />
            </>
          )}
        </Section>
      )}
      {tab === 'cashbook' && (
        <div className="finance-cashbook-layout">
          <div className="finance-view-switch" role="tablist" aria-label="Income and expense views">
            <button type="button" className={cashbookView === 'register' ? 'active' : ''} onClick={() => setCashbookView('register')}>
              Cashbook register
            </button>
            <button type="button" className={cashbookView === 'record' ? 'active' : ''} onClick={() => setCashbookView('record')}>
              Record transaction
            </button>
          </div>
          {cashbookView === 'record' && (
          <Section title="Record income or expense">
            <form className="finance-form" onSubmit={saveEntry}>
              <Field label="Entry type">
                <select
                  value={entry.entryType}
                  onChange={(e) =>
                    setEntry({ ...entry, entryType: e.target.value })
                  }
                >
                  <option>EXPENSE</option>
                  <option>INCOME</option>
                </select>
              </Field>
              <Field label="Accounting unit">
                <select
                  required
                  value={entry.accountingUnitId}
                  onChange={(e) =>
                    setEntry({
                      ...entry,
                      accountingUnitId: e.target.value,
                      fundId: '',
                      transactionAccountId: '',
                    })
                  }
                >
                  <option value="">Select</option>
                  {foundation.accountingUnits
                    .filter((x) => x.active)
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field label="Fund">
                <select
                  required
                  value={entry.fundId}
                  onChange={(e) =>
                    setEntry({
                      ...entry,
                      fundId: e.target.value,
                      transactionAccountId: '',
                    })
                  }
                >
                  <option value="">Select</option>
                  {foundation.funds
                    .filter(
                      (x) =>
                        x.active &&
                        String(x.accounting_unit_id) ===
                          String(entry.accountingUnitId)
                    )
                    .map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                </select>
              </Field>
              {entry.entryType === 'INCOME' && (
                <Field label="Received in account" help="Select the bank, UPI or cash account in which this income was actually received.">
                  <select
                    required
                    value={entry.transactionAccountId}
                    onChange={(e) =>
                      setEntry({ ...entry, transactionAccountId: e.target.value })
                    }
                  >
                    <option value="">Select</option>
                    {foundation.transactionAccounts
                      .filter(
                        (x) =>
                          x.active &&
                          String(x.accounting_unit_id) ===
                            String(entry.accountingUnitId) &&
                          (x.fund_id == null ||
                            String(x.fund_id) === String(entry.fundId))
                      )
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                  </select>
                </Field>
              )}
              <div className="finance-two">
                <Field label="Category">
                  <input
                    required
                    value={entry.category}
                    onChange={(e) =>
                      setEntry({ ...entry, category: e.target.value })
                    }
                  />
                </Field>
                <Field label="Date">
                  <input
                    required
                    type="date"
                    value={entry.transactionDate}
                    onChange={(e) =>
                      setEntry({ ...entry, transactionDate: e.target.value })
                    }
                  />
                </Field>
              </div>
              <Field label="Title">
                <input
                  required
                  value={entry.title}
                  onChange={(e) =>
                    setEntry({ ...entry, title: e.target.value })
                  }
                />
              </Field>
              <div className="finance-two">
                <Field label="Party">
                  <input
                    value={entry.payerOrPayee}
                    onChange={(e) =>
                      setEntry({ ...entry, payerOrPayee: e.target.value })
                    }
                  />
                </Field>
                <Field label="Amount">
                  <input
                    required
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={entry.amount}
                    onChange={(e) =>
                      setEntry({ ...entry, amount: e.target.value })
                    }
                  />
                </Field>
              </div>
              <Field label="Description">
                <textarea
                  value={entry.description}
                  onChange={(e) =>
                    setEntry({ ...entry, description: e.target.value })
                  }
                />
              </Field>
              {entry.entryType === 'INCOME' && (
                <>
                  <Field label="Payment mode">
                    <select
                      value={entry.paymentMode}
                      onChange={(e) =>
                        setEntry({ ...entry, paymentMode: e.target.value })
                      }
                    >
                      {['BANK_TRANSFER', 'UPI', 'NEFT', 'IMPS', 'RTGS', 'CHEQUE', 'CASH', 'OTHER'].map((mode) => (
                        <option key={mode}>{mode}</option>
                      ))}
                    </select>
                  </Field>
                  {entry.paymentMode === 'CHEQUE' ? (
                    <Field label="Cheque number">
                      <input required value={entry.chequeNumber} onChange={(e) => setEntry({ ...entry, chequeNumber: e.target.value })} />
                    </Field>
                  ) : entry.paymentMode === 'CASH' ? (
                    <Field label="Cash voucher number">
                      <input required value={entry.cashVoucherNumber} onChange={(e) => setEntry({ ...entry, cashVoucherNumber: e.target.value })} />
                    </Field>
                  ) : (
                    <Field label="Transaction reference">
                      <input required value={entry.transactionReference} onChange={(e) => setEntry({ ...entry, transactionReference: e.target.value })} />
                    </Field>
                  )}
                </>
              )}
              <Field label="Invoice / source evidence">
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={(e) => setEntryEvidence(e.target.files?.[0] || null)}
                />
              </Field>
              <button className="btn btn-primary">Record entry</button>
            </form>
          </Section>
          )}
          {cashbookView === 'register' && (
          <Section title="Income and expense cashbook">
            <p className="finance-help">
              Review income and expenses here. Expense payment account, mode and
              bank reference are captured after approval through <strong>Mark paid</strong>.
            </p>
            <div className="finance-cashbook-filters">
              <Field label="Search">
                <input
                  placeholder="Title, party, fund, account or reference"
                  value={cashbookFilters.search}
                  onChange={(e) => {
                    setCashbookPage(0);
                    setCashbookFilters({ ...cashbookFilters, search: e.target.value });
                  }}
                />
              </Field>
              <Field label="Entry type">
                <select value={cashbookFilters.type} onChange={(e) => { setCashbookPage(0); setCashbookFilters({ ...cashbookFilters, type: e.target.value }); }}>
                  <option value="ALL">All types</option>
                  <option value="INCOME">Income</option>
                  <option value="EXPENSE">Expense</option>
                </select>
              </Field>
              <Field label="Status">
                <select value={cashbookFilters.status} onChange={(e) => { setCashbookPage(0); setCashbookFilters({ ...cashbookFilters, status: e.target.value }); }}>
                  <option value="ALL">All statuses</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="UNPAID">Unpaid</option>
                  <option value="PAID">Paid</option>
                </select>
              </Field>
              <Field label="From"><input type="date" value={cashbookFilters.from} onChange={(e) => { setCashbookPage(0); setCashbookFilters({ ...cashbookFilters, from: e.target.value }); }} /></Field>
              <Field label="To"><input type="date" value={cashbookFilters.to} onChange={(e) => { setCashbookPage(0); setCashbookFilters({ ...cashbookFilters, to: e.target.value }); }} /></Field>
              <Field label="Sort by">
                <select value={cashbookFilters.sortBy} onChange={(e) => setCashbookFilters({ ...cashbookFilters, sortBy: e.target.value })}>
                  <option value="transactionDate">Date</option>
                  <option value="title">Title</option>
                  <option value="type">Type</option>
                  <option value="fund">Fund</option>
                  <option value="amount">Amount</option>
                  <option value="status">Status</option>
                </select>
              </Field>
              <Field label="Order">
                <select value={cashbookFilters.direction} onChange={(e) => setCashbookFilters({ ...cashbookFilters, direction: e.target.value })}>
                  <option value="desc">Newest / highest first</option>
                  <option value="asc">Oldest / lowest first</option>
                </select>
              </Field>
              <Field label="Rows per page">
                <select value={cashbookFilters.size} onChange={(e) => { setCashbookPage(0); setCashbookFilters({ ...cashbookFilters, size: Number(e.target.value) }); }}>
                  {[5, 10, 20, 50].map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </Field>
              <button type="button" className="btn btn-secondary finance-clear-filter" onClick={() => {
                setCashbookPage(0);
                setCashbookFilters({ type: 'ALL', status: 'ALL', search: '', from: '', to: '', sortBy: 'transactionDate', direction: 'desc', size: 5 });
              }}>Clear filters</button>
            </div>
            <div className="table-responsive finance-mobile-cards">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Fund</th>
                    <th>Account / mode</th>
                    <th>Status</th>
                    <th className="text-end">Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cashbook.content?.map((x) => (
                    <tr key={x.id}>
                      <td data-label="Date">{x.transaction_date}</td>
                      <td data-label="Type">{x.entry_type}</td>
                      <td data-label="Title">{x.title}</td>
                      <td data-label="Fund">{x.fund_name}</td>
                      <td data-label="Account / mode">
                        {x.account_name || 'Payment not recorded'}
                        {x.payment_mode && <small>{x.payment_mode} · {x.transaction_reference || x.cheque_number || x.cash_voucher_number}</small>}
                      </td>
                      <td data-label="Status">
                        {x.approval_status} / {x.payment_status}
                      </td>
                      <td data-label="Amount" className="text-end">{money(x.amount)}</td>
                      <td data-label="Actions">
                        <div className="finance-actions">
                          {x.entry_type === 'EXPENSE' &&
                            x.approval_status === 'SUBMITTED' && (
                              <>
                                <button
                                  onClick={() => decideEntry(x.id, 'APPROVE')}
                                >
                                  Approve
                                </button>
                                <button
                                  className="danger"
                                  onClick={() => decideEntry(x.id, 'REJECT')}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          {x.entry_type === 'EXPENSE' &&
                            x.approval_status === 'APPROVED' &&
                            x.payment_status !== 'PAID' && (
                              <button
                                onClick={() => openExpensePayment(x)}
                              >
                                Mark paid
                              </button>
                            )}
                          {x.has_source_evidence && (
                            <button type="button" onClick={() => openCashbookEvidence(x.id, 'SOURCE')}>Source evidence</button>
                          )}
                          {x.has_payment_evidence && (
                            <button type="button" onClick={() => openCashbookEvidence(x.id, 'PAYMENT')}>Payment proof</button>
                          )}
                          <button type="button" onClick={() => showCashbookHistory(x)}>View details</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!cashbook.content?.length && <div className="finance-empty">No cashbook entries match these filters.</div>}
            <Pager data={cashbook} page={cashbookPage} setPage={setCashbookPage} />
            {selectedCashbookEntry && createPortal(
              <div className="finance-modal-backdrop" role="presentation" onMouseDown={(e) => {
                if (e.target === e.currentTarget) setSelectedCashbookEntry(null);
              }}>
              <section className="finance-modal finance-cashbook-detail" role="dialog" aria-modal="true" aria-labelledby="cashbook-detail-title">
                <header>
                  <div>
                    <span className="finance-status">{selectedCashbookEntry.entry_type}</span>
                    <h3 id="cashbook-detail-title">{selectedCashbookEntry.title}</h3>
                    <p>{selectedCashbookEntry.fund_name} · {money(selectedCashbookEntry.amount)}</p>
                  </div>
                  <button className="finance-modal-close" type="button" aria-label="Close details" onClick={() => setSelectedCashbookEntry(null)}>×</button>
                </header>
                <div className="finance-detail-grid">
                  <div><small>Transaction date</small><strong>{selectedCashbookEntry.transaction_date}</strong></div>
                  <div><small>Party</small><strong>{selectedCashbookEntry.payer_or_payee || 'Not specified'}</strong></div>
                  <div><small>Approval</small><strong>{selectedCashbookEntry.approval_status}</strong></div>
                  <div><small>Payment</small><strong>{selectedCashbookEntry.payment_status}</strong></div>
                  <div><small>Account</small><strong>{selectedCashbookEntry.account_name || 'Captured after approval'}</strong></div>
                  <div><small>Mode / reference</small><strong>{selectedCashbookEntry.payment_mode ? `${selectedCashbookEntry.payment_mode} · ${selectedCashbookEntry.transaction_reference || selectedCashbookEntry.cheque_number || selectedCashbookEntry.cash_voucher_number || 'No reference'}` : 'Captured when marked paid'}</strong></div>
                </div>
                <h4>Activity history</h4>
                <ol className="finance-cashbook-history">
                  {(cashbookHistories[selectedCashbookEntry.id] || []).map((h) => (
                    <li key={h.id}>
                      <strong>{String(h.action).replaceAll('_', ' ')}</strong>
                      <span>{h.actor_name} · {dateTime(h.created_at)}</span>
                      {h.remarks && <span>{h.remarks}</span>}
                      {h.details && <span>{h.details}</span>}
                      {h.attachment_original_name && <span>Attachment: {h.attachment_original_name}</span>}
                    </li>
                  ))}
                </ol>
              </section>
              </div>,
              document.body
            )}
            {payingEntry && (
              <form className="finance-payment-panel" onSubmit={completeExpensePayment}>
                <header>
                  <div>
                    <strong>Record payment: {payingEntry.title}</strong>
                    <p>{money(payingEntry.amount)} · approval complete</p>
                  </div>
                  <button type="button" onClick={() => setPayingEntry(null)}>Cancel</button>
                </header>
                <div className="finance-two">
                  <Field label="Paid from account">
                    <select required value={expensePayment.transactionAccountId} onChange={(e) => setExpensePayment({ ...expensePayment, transactionAccountId: e.target.value })}>
                      <option value="">Select</option>
                      {foundation.transactionAccounts
                        .filter((x) => x.active && String(x.accounting_unit_id) === String(payingEntry.accounting_unit_id) && (x.fund_id == null || String(x.fund_id) === String(payingEntry.fund_id)))
                        .map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Payment date"><input required type="date" value={expensePayment.paymentDate} onChange={(e) => setExpensePayment({ ...expensePayment, paymentDate: e.target.value })} /></Field>
                </div>
                <Field label="Payment mode">
                  <select value={expensePayment.paymentMode} onChange={(e) => setExpensePayment({ ...expensePayment, paymentMode: e.target.value })}>
                    {['BANK_TRANSFER', 'UPI', 'NEFT', 'IMPS', 'RTGS', 'CHEQUE', 'CASH', 'OTHER'].map((mode) => <option key={mode}>{mode}</option>)}
                  </select>
                </Field>
                {expensePayment.paymentMode === 'CHEQUE' ? (
                  <Field label="Cheque number"><input required value={expensePayment.chequeNumber} onChange={(e) => setExpensePayment({ ...expensePayment, chequeNumber: e.target.value })} /></Field>
                ) : expensePayment.paymentMode === 'CASH' ? (
                  <Field label="Cash voucher number"><input required value={expensePayment.cashVoucherNumber} onChange={(e) => setExpensePayment({ ...expensePayment, cashVoucherNumber: e.target.value })} /></Field>
                ) : (
                  <Field label="Transaction reference"><input required value={expensePayment.transactionReference} onChange={(e) => setExpensePayment({ ...expensePayment, transactionReference: e.target.value })} /></Field>
                )}
                <Field label="Payment remarks"><textarea value={expensePayment.remarks} onChange={(e) => setExpensePayment({ ...expensePayment, remarks: e.target.value })} /></Field>
                <Field label="Payment proof"><input type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={(e) => setExpensePaymentEvidence(e.target.files?.[0] || null)} /></Field>
                <button className="btn btn-primary">Confirm expense payment</button>
              </form>
            )}
          </Section>
          )}
        </div>
      )}
      {tab === 'setup' && (
        <FinanceSetup
          foundation={foundation}
          communityId={communityId}
          structureUnits={structureUnits}
          get={get}
          post={post}
          put={put}
          remove={remove}
          reload={loadFoundation}
          setError={setError}
          setMessage={setMessage}
        />
      )}
      {tab === 'dues' && (
        <div className="finance-resident-sections">
        <Section title="Compulsory charges">
          <p className="finance-help">
            Amounts billed to your home by the community.
          </p>
          <div className="finance-cards">
            {dues.content?.map((x) => (
              <article key={x.id}>
                <header>
                  <strong>{x.campaign_title}</strong>
                  <span>{x.status}</span>
                </header>
                <p>
                  {x.property_number} · due {x.due_date || 'not specified'}
                </p>
                <h3>{money(x.outstanding_amount)}</h3>
                <small>
                  Charge {x.charge_number} · paid {money(x.paid_amount)}
                </small>
                {['PAYMENT_SUBMITTED', 'UNDER_VERIFICATION'].includes(
                  x.payment_submission_status
                ) ? (
                  <div className="finance-review-note">
                    <strong>Pending community verification</strong>
                    <span>Submitted {dateTime(x.payment_submitted_at)}. A second payment cannot be submitted for this charge.</span>
                  </div>
                ) : x.payment_submission_status === 'APPROVED' ? (
                  <div className="finance-review-note"><strong>Payment approved</strong></div>
                ) : (
                  <>
                    {['CORRECTION_REQUESTED', 'REJECTED'].includes(
                      x.payment_submission_status
                    ) && (
                      <div className="finance-review-note warning">
                        <strong>{x.payment_submission_status === 'REJECTED' ? 'Payment rejected' : 'Correction requested'}</strong>
                        <span>{x.payment_reviewer_remarks || 'Please correct the payment details and submit again.'}</span>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setPayment({
                          ...payment,
                          campaignId: x.collection_campaign_id,
                          chargeId: x.id,
                          propertyId: x.property_id,
                          amount: x.outstanding_amount,
                        });
                        setTab('pay');
                      }}
                    >
                      {['CORRECTION_REQUESTED', 'REJECTED'].includes(x.payment_submission_status)
                        ? 'Correct and resubmit'
                        : 'Submit payment details'}
                    </button>
                  </>
                )}
              </article>
            ))}
          </div>
          <Pager data={dues} page={page} setPage={setPage} />
        </Section>
        <Section title="Voluntary contributions">
          <p className="finance-help">
            Optional community or event contributions available to your home.
            These do not create an outstanding bill.
          </p>
          <div className="finance-cards">
            {residentCampaigns.content
              ?.filter((x) => x.contribution_type === 'VOLUNTARY')
              .map((x) => (
                <article key={`${x.id}-${x.property_id}`}>
                  <header>
                    <strong>{x.title}</strong>
                    <span>OPTIONAL</span>
                  </header>
                  <p>
                    {x.description || 'Community contribution'} ·{' '}
                    {x.property_name || 'Your home'}
                  </p>
                  <h3>
                    {x.minimum_contribution
                      ? `From ${money(x.minimum_contribution)}`
                      : 'Choose an amount'}
                  </h3>
                  <small>
                    Available until {x.due_date || 'further notice'}
                  </small>
                  <button
                    onClick={() => {
                      setPayment({
                        ...payment,
                        campaignId: x.id,
                        chargeId: '',
                        propertyId: x.property_id,
                        amount: x.minimum_contribution || '',
                      });
                      setTab('pay');
                    }}
                  >
                    Contribute
                  </button>
                </article>
              ))}
            {!residentCampaigns.content?.some(
              (x) => x.contribution_type === 'VOLUNTARY'
            ) && (
              <p className="finance-empty">
                No voluntary contributions are open.
              </p>
            )}
          </div>
        </Section>
        </div>
      )}
      {tab === 'pay' && (
        <Section title="Submit payment details">
          <form
            className="finance-form finance-payment"
            onSubmit={submitPayment}
          >
            <Field label="Campaign and home">
              <select
                required
                value={
                  payment.campaignId && payment.propertyId
                    ? `${payment.campaignId}:${payment.propertyId}`
                    : ''
                }
                onChange={(e) => {
                  const selected = payableCampaigns.find(
                    (x) => `${x.id}:${x.property_id}` === e.target.value
                  );
                  setPayment({
                    ...payment,
                    campaignId: selected?.id || '',
                    propertyId: selected?.property_id || '',
                    chargeId: selected?.charge_id || '',
                    amount:
                      selected?.outstanding_amount ||
                      selected?.minimum_contribution ||
                      '',
                    transactionAccountId: '',
                  });
                }}
              >
                <option value="">Select</option>
                {payableCampaigns.map((x) => (
                  <option
                    key={`${x.id}-${x.property_id}`}
                    value={`${x.id}:${x.property_id}`}
                  >
                    {x.title} · {x.property_name || 'Your home'} ·{' '}
                    {x.contribution_type === 'VOLUNTARY'
                      ? 'optional'
                      : 'compulsory'}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Payment account">
              <select
                required
                value={payment.transactionAccountId}
                onChange={(e) =>
                  setPayment({
                    ...payment,
                    transactionAccountId: e.target.value,
                  })
                }
              >
                <option value="">Select</option>
                {accounts
                  .filter((x) => x.active)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} {x.upi_id || x.masked_account_number || ''}
                    </option>
                  ))}
              </select>
            </Field>
            {selectedPaymentAccount && (
              <div className="finance-payment-instructions">
                <div>
                  <strong>Pay to: {selectedPaymentAccount.name}</strong>
                  <dl className="finance-detail-grid">
                    <div><dt>Bank</dt><dd>{selectedPaymentAccount.bank_name || 'Not applicable'}</dd></div>
                    <div><dt>Account holder</dt><dd>{selectedPaymentAccount.account_holder_name || selectedPaymentAccount.upi_payee_name || 'Not configured'}</dd></div>
                    <div><dt>Account number</dt><dd>{selectedPaymentAccount.account_number || selectedPaymentAccount.masked_account_number || 'Not configured'}</dd></div>
                    <div><dt>IFSC</dt><dd>{selectedPaymentAccount.ifsc || 'Not configured'}</dd></div>
                    <div><dt>UPI ID</dt><dd>{selectedPaymentAccount.upi_id || 'Not configured'}</dd></div>
                  </dl>
                  {!selectedPaymentAccount.account_number &&
                    ['NEFT', 'IMPS', 'RTGS', 'BANK_TRANSFER'].includes(payment.paymentMode) && (
                      <p className="finance-review-note warning">
                        The complete bank account number has not been configured. Please contact the community administrator before transferring.
                      </p>
                    )}
                  <small>Confirm the destination shown here before making payment.</small>
                </div>
                {selectedPaymentAccount.has_qr && (
                  <ProtectedImage
                    url={`/api/finance/transaction-accounts/${selectedPaymentAccount.id}/qr-code`}
                    alt={`Payment QR for ${selectedPaymentAccount.name}`}
                  />
                )}
              </div>
            )}
            <div className="finance-two">
              <Field label="Payment mode">
                <select
                  value={payment.paymentMode}
                  onChange={(e) =>
                    setPayment({ ...payment, paymentMode: e.target.value })
                  }
                >
                  {[
                    'UPI',
                    'NEFT',
                    'IMPS',
                    'RTGS',
                    'BANK_TRANSFER',
                    'CHEQUE',
                    'OTHER',
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </Field>
              <Field label="Amount">
                <input
                  required
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={payment.amount}
                  onChange={(e) =>
                    setPayment({ ...payment, amount: e.target.value })
                  }
                />
              </Field>
            </div>
            {payment.paymentMode === 'CHEQUE' && (
              <div className="finance-two">
                <Field label="Cheque number" help="Enter the number printed on the cheque. The payment remains under verification until the cheque is cleared.">
                  <input required value={payment.chequeNumber} onChange={(e) => setPayment({ ...payment, chequeNumber: e.target.value })} />
                </Field>
                <Field label="Cheque date" help="Enter the date written on the cheque.">
                  <input required type="date" value={payment.chequeDate} onChange={(e) => setPayment({ ...payment, chequeDate: e.target.value })} />
                </Field>
                <Field label="Bank name">
                  <input required value={payment.bankName} onChange={(e) => setPayment({ ...payment, bankName: e.target.value })} />
                </Field>
              </div>
            )}
            <Field label="Transaction reference">
              <input
                required={['UPI', 'NEFT', 'IMPS', 'RTGS', 'BANK_TRANSFER'].includes(payment.paymentMode)}
                value={payment.transactionReference}
                onChange={(e) =>
                  setPayment({
                    ...payment,
                    transactionReference: e.target.value,
                  })
                }
              />
            </Field>
            <Field label="Payer name">
              <input
                required
                value={payment.payerName}
                onChange={(e) =>
                  setPayment({ ...payment, payerName: e.target.value })
                }
              />
            </Field>
            <Field label="Resident remarks" help="Optional note for the community administrator, for example the payer relationship, transfer clarification or correction response.">
              <textarea
                value={payment.remarks}
                placeholder="Optional payment note"
                onChange={(e) => setPayment({ ...payment, remarks: e.target.value })}
              />
            </Field>
            <Field label="Payment screenshot / transfer proof (optional)">
              <input
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                onChange={(event) =>
                  setEvidence(event.target.files?.[0] || null)
                }
              />
            </Field>
            <button className="btn btn-primary">Submit for verification</button>
          </form>
        </Section>
      )}
      {tab === 'payment-history' && (
        <div className="finance-resident-sections">
          <Section title="Payment submissions">
            <div className="finance-filter-grid">
              <Field label="Status">
                <select
                  value={paymentHistoryFilters.status}
                  onChange={(event) => {
                    setPaymentHistoryPage(0);
                    setPaymentHistoryFilters({
                      ...paymentHistoryFilters,
                      status: event.target.value,
                    });
                  }}
                >
                  <option value="ALL">All statuses</option>
                  <option value="PAYMENT_SUBMITTED">Submitted</option>
                  <option value="UNDER_VERIFICATION">Under verification</option>
                  <option value="CORRECTION_REQUESTED">Correction requested</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </Field>
              <Field label="Search">
                <input
                  value={paymentHistoryFilters.search}
                  placeholder="Collection, home, reference or mode"
                  onChange={(event) => {
                    setPaymentHistoryPage(0);
                    setPaymentHistoryFilters({
                      ...paymentHistoryFilters,
                      search: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Submitted from">
                <input
                  type="date"
                  value={paymentHistoryFilters.from}
                  onChange={(event) => {
                    setPaymentHistoryPage(0);
                    setPaymentHistoryFilters({
                      ...paymentHistoryFilters,
                      from: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Submitted to">
                <input
                  type="date"
                  value={paymentHistoryFilters.to}
                  onChange={(event) => {
                    setPaymentHistoryPage(0);
                    setPaymentHistoryFilters({
                      ...paymentHistoryFilters,
                      to: event.target.value,
                    });
                  }}
                />
              </Field>
              <Field label="Rows per page">
                <select
                  value={paymentHistoryFilters.size}
                  onChange={(event) => {
                    setPaymentHistoryPage(0);
                    setPaymentHistoryFilters({
                      ...paymentHistoryFilters,
                      size: Number(event.target.value),
                    });
                  }}
                >
                  {[5, 10, 20, 50].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setPaymentHistoryPage(0);
                  setPaymentHistoryFilters({
                    status: 'ALL',
                    search: '',
                    from: '',
                    to: '',
                    size: 5,
                  });
                }}
              >
                Clear filters
              </button>
            </div>
            <div className="finance-cards">
              {paymentHistory.content?.map((item) => (
                <article key={item.id}>
                  <header>
                    <strong>{item.campaign_title}</strong>
                    <span>{item.verification_status}</span>
                  </header>
                  <p>{item.property_name || item.property_number} · {item.payment_mode} · {money(item.amount_submitted)}</p>
                  <small>Submitted {dateTime(item.submitted_at)}</small>
                  <p>Transaction reference: {item.transaction_reference || 'Not supplied'}</p>
                  {item.cheque_status && <p>Cheque status: {item.cheque_status}</p>}
                  {item.reviewer_remarks && (
                    <div className={item.verification_status === 'CORRECTION_REQUESTED' ? 'finance-review-note warning' : 'finance-review-note'}>
                      <strong>Administrator remarks</strong>
                      <span>{item.reviewer_remarks}</span>
                    </div>
                  )}
                  {item.receipt_id && item.receipt_status === 'ISSUED' && (
                    <button onClick={() => downloadReceipt({ id: item.receipt_id, receipt_number: item.receipt_number })}>
                      Download receipt
                    </button>
                  )}
                  <div className="finance-actions">
                    {item.has_evidence && (
                      <button type="button" onClick={() => viewPaymentEvidence(item)}>
                        View submitted proof
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        paymentHistories[item.id]
                          ? setPaymentHistories((current) => {
                              const next = { ...current };
                              delete next[item.id];
                              return next;
                            })
                          : loadPaymentHistory(item.id)
                      }
                    >
                      {paymentHistories[item.id] ? 'Close history' : 'View full history'}
                    </button>
                  </div>
                  {paymentHistories[item.id] && <PaymentTimeline items={paymentHistories[item.id]} />}
                </article>
              ))}
              {!paymentHistory.content?.length && <p className="finance-empty">No payment submissions yet.</p>}
            </div>
            <Pager
              data={paymentHistory}
              page={paymentHistoryPage}
              setPage={setPaymentHistoryPage}
            />
          </Section>
        </div>
      )}
      {showOnBehalfPayment && createPortal(
        <div className="finance-modal-backdrop" role="presentation" onMouseDown={(e) => {
          if (e.target === e.currentTarget) setShowOnBehalfPayment(false);
        }}>
          <section className="finance-modal finance-on-behalf-modal" role="dialog" aria-modal="true" aria-labelledby="on-behalf-title">
            <header>
              <div>
                <span className="finance-status">OFFICE PAYMENT</span>
                <h3 id="on-behalf-title">Record payment on behalf of resident</h3>
                <p>Use this when the office receives cash or records a bank/UPI payment for a resident who cannot submit it.</p>
              </div>
              <button className="finance-modal-close" type="button" aria-label="Close" onClick={() => setShowOnBehalfPayment(false)}>×</button>
            </header>
            <form className="finance-form" onSubmit={saveOnBehalfPayment}>
              <Field label="Home / property">
                <select required value={onBehalfPayment.propertyId} onChange={(e) => selectOnBehalfProperty(e.target.value)}>
                  <option value="">Select resident home</option>
                  {properties.filter((item) => item.active !== false).map((item) => (
                    <option key={item.id} value={item.id}>
                      {[item.structure_unit_code || item.structure_unit_name, item.display_name || item.property_number].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Outstanding collection">
                <select required value={onBehalfPayment.chargeId} onChange={(e) => selectOnBehalfCharge(e.target.value)}>
                  <option value="">Select outstanding charge</option>
                  {onBehalfCharges.map((item) => (
                    <option key={item.id} value={item.id}>{item.campaign_title} · {money(item.outstanding_amount)}</option>
                  ))}
                </select>
              </Field>
              <div className="finance-two">
                <Field label="Payment mode">
                  <select value={onBehalfPayment.paymentMode} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, paymentMode: e.target.value, transactionAccountId: '' })}>
                    {['CASH', 'UPI', 'BANK_TRANSFER', 'NEFT', 'IMPS', 'RTGS', 'CHEQUE', 'OTHER'].map((mode) => <option key={mode}>{mode}</option>)}
                  </select>
                </Field>
                <Field label="Receiving account">
                  <select required value={onBehalfPayment.transactionAccountId} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, transactionAccountId: e.target.value })}>
                    <option value="">Select account</option>
                    {foundation.transactionAccounts.filter((account) => {
                      const charge = onBehalfCharges.find((item) => String(item.id) === String(onBehalfPayment.chargeId));
                      return account.active && charge && String(account.accounting_unit_id) === String(charge.accounting_unit_id) && (account.fund_id == null || String(account.fund_id) === String(charge.fund_id));
                    }).map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
                  </select>
                </Field>
              </div>
              <div className="finance-two">
                <Field label="Amount"><input required type="number" min="0.01" step="0.01" value={onBehalfPayment.amount} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, amount: e.target.value })} /></Field>
                <Field label="Payment date"><input required type="date" value={onBehalfPayment.transactionDate} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, transactionDate: e.target.value })} /></Field>
              </div>
              <Field label="Payer name"><input required value={onBehalfPayment.payerName} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, payerName: e.target.value })} /></Field>
              {onBehalfPayment.paymentMode === 'CASH' ? (
                <Field label="Cash voucher number"><input required value={onBehalfPayment.cashVoucherNumber} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, cashVoucherNumber: e.target.value })} /></Field>
              ) : onBehalfPayment.paymentMode === 'CHEQUE' ? (
                <div className="finance-two">
                  <Field label="Bank name"><input required value={onBehalfPayment.bankName} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, bankName: e.target.value })} /></Field>
                  <Field label="Cheque number"><input required value={onBehalfPayment.chequeNumber} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, chequeNumber: e.target.value })} /></Field>
                  <Field label="Cheque date"><input required type="date" value={onBehalfPayment.chequeDate} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, chequeDate: e.target.value })} /></Field>
                </div>
              ) : (
                <Field label="Transaction reference"><input required value={onBehalfPayment.transactionReference} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, transactionReference: e.target.value })} /></Field>
              )}
              <Field label="Payment proof (optional)"><input type="file" accept=".png,.jpg,.jpeg,.pdf" onChange={(e) => setOnBehalfEvidence(e.target.files?.[0] || null)} /></Field>
              <Field label="Remarks"><textarea value={onBehalfPayment.remarks} onChange={(e) => setOnBehalfPayment({ ...onBehalfPayment, remarks: e.target.value })} /></Field>
              <button className="btn btn-primary">Record and approve payment</button>
            </form>
          </section>
        </div>,
        document.body
      )}
    </div>
  );
}
function FinanceSetup({
  foundation,
  communityId,
  structureUnits,
  get,
  post,
  put,
  remove,
  reload,
  setError,
  setMessage,
}) {
  const [editingUnitId, setEditingUnitId] = useState(null),
    [editingFundId, setEditingFundId] = useState(null),
    [editingAccountId, setEditingAccountId] = useState(null),
    [unit, setUnit] = useState({
      code: '',
      name: '',
      unitType: 'COMMUNITY',
      parentId: '',
      structureUnitIds: [],
      active: true,
    }),
    [fund, setFund] = useState({
      code: '',
      name: '',
      fundType: 'MAINTENANCE',
      accountingUnitId: '',
      description: '',
      active: true,
    }),
    [account, setAccount] = useState({
      name: '',
      accountType: 'BANK',
      accountingUnitId: '',
      fundId: '',
      upiId: '',
      maskedAccountNumber: '',
      accountNumber: '',
      maskAccountNumber: false,
      ifsc: '',
      bankName: '',
      accountHolderName: '',
      upiPayeeName: '',
      effectiveFrom: '',
      effectiveTo: '',
      active: true,
      supportedPaymentModes: [
        'UPI',
        'BANK_TRANSFER',
        'CHEQUE',
        'CASH',
        'OTHER',
      ],
    }),
    [users, setUsers] = useState([]),
    [editingScopeId, setEditingScopeId] = useState(null),
    [scope, setScope] = useState({
      userId: '',
      accountingUnitId: '',
      fundId: '',
      active: true,
    });
  useEffect(() => {
    if (!communityId) return;
    get('/api/admin/users/search', {
      params: { communityId, page: 0, size: 500 },
    })
      .then((result) => setUsers(result.content || result || []))
      .catch((x) => setError(x.message));
  }, [communityId, get, setError]);
  const save = async (type, data, reset, id = null) => {
    try {
      const payload = {
        ...data,
        communityId,
        accountingUnitId: data.accountingUnitId
          ? Number(data.accountingUnitId)
          : undefined,
        fundId: data.fundId ? Number(data.fundId) : null,
        parentId: data.parentId ? Number(data.parentId) : null,
        active: data.active !== false,
        structureUnitIds: data.structureUnitIds || [],
      };
      if (id) await put(`/api/finance/${type}/${id}`, payload);
      else await post(`/api/finance/${type}`, payload);
      reset();
      setMessage(id ? 'Finance configuration updated.' : 'Finance configuration saved.');
      reload();
    } catch (x) {
      setError(x.message);
    }
  };
  const removeItem = async (type, id, label, clearEdit) => {
    if (!window.confirm(`Delete ${label}? This is allowed only when it has no financial activity.`)) return;
    try {
      await remove(`/api/finance/${type}/${id}`, {
        params: { communityId },
      });
      clearEdit();
      setMessage(`${label} deleted.`);
      reload();
    } catch (x) {
      setError(x.message);
    }
  };
  const uploadQr = async (accountId, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await post(
        `/api/finance/transaction-accounts/${accountId}/qr-code`,
        form
      );
      setMessage('Payment QR code uploaded.');
      reload();
    } catch (x) {
      setError(x.message);
    }
  };
  return (
    <>
      <div className="finance-guide">
        <strong>Simple example for one community</strong>
        <span>1. Accounting area: <b>Whole Society</b></span>
        <span>2. Purpose/fund: <b>Monthly Maintenance</b> or <b>Navratri Event</b></span>
        <span>3. Receiving account: <b>Society Bank / UPI</b></span>
        <span>4. Create a collection and charge the selected homes.</span>
        <small>You need multiple accounting areas only when divisions maintain separate books or bank accounts.</small>
      </div>
      <div className="finance-setup">
      <Section title="1. Accounting areas">
        <p className="finance-help">Who keeps a separate set of books? For most communities, create only “Whole Community”.</p>
        <form
          className="finance-form"
          onSubmit={(e) => {
            e.preventDefault();
            save('accounting-units', unit, () => {
              setUnit({ code: '', name: '', unitType: 'COMMUNITY', parentId: '', structureUnitIds: [], active: true });
              setEditingUnitId(null);
            }, editingUnitId);
          }}
        >
          <div className="finance-two">
            <Field label="Accounting area type" help="Choose Community for shared books or Division/Wing when this area maintains separate funds or accounts.">
              <select value={unit.unitType} onChange={(e) => setUnit({ ...unit, unitType: e.target.value })}>
                {['COMMUNITY', 'DIVISION', 'SOCIETY', 'WING', 'BLOCK', 'OTHER'].map((value) => <option key={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="Parent accounting area" help="Select a containing accounting area only when financial books are nested; otherwise leave it blank.">
              <select value={unit.parentId} onChange={(e) => setUnit({ ...unit, parentId: e.target.value })}>
                <option value="">None</option>
                {foundation.accountingUnits.filter((x) => x.id !== editingUnitId).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Code">
            <input
              required
              value={unit.code}
              onChange={(e) => setUnit({ ...unit, code: e.target.value })}
            />
          </Field>
          <Field label="Name">
            <input
              required
              value={unit.name}
              onChange={(e) => setUnit({ ...unit, name: e.target.value })}
            />
          </Field>
          <Field label="Mapped divisions / wings" help="Select the real address groups whose properties belong to this accounting area. Use Ctrl or Command to select more than one.">
            <select
              multiple
              size="4"
              value={unit.structureUnitIds.map(String)}
              onChange={(e) => setUnit({ ...unit, structureUnitIds: Array.from(e.target.selectedOptions, (option) => Number(option.value)) })}
            >
              {structureUnits.filter((x) => x.active).map((x) => <option key={x.id} value={x.id}>{x.name} ({x.code})</option>)}
            </select>
          </Field>
          <label className="finance-check"><input type="checkbox" checked={unit.active} onChange={(e) => setUnit({ ...unit, active: e.target.checked })} /> Active</label>
          <button className="btn btn-primary">{editingUnitId ? 'Update accounting area' : 'Add accounting area'}</button>
        </form>
        {foundation.accountingUnits.map((x) => (
          <div key={x.id} className="finance-setup-item">
            <span><strong>{x.name}</strong><small>{x.code}</small></span>
            <div className="finance-actions">
              <button type="button" onClick={() => { setEditingUnitId(x.id); setUnit({ code: x.code, name: x.name, unitType: x.unit_type || 'COMMUNITY', parentId: x.parent_accounting_unit_id ? String(x.parent_accounting_unit_id) : '', active: x.active, structureUnitIds: foundation.unitStructureMappings.filter((m) => m.accounting_unit_id === x.id).map((m) => m.structure_unit_id) }); }}>Edit</button>
              <button type="button" className="danger" onClick={() => removeItem('accounting-units', x.id, x.name, () => setEditingUnitId(null))}>Delete</button>
            </div>
          </div>
        ))}
      </Section>
      <Section title="2. Funds / purposes">
        <p className="finance-help">Why is money collected or spent? Examples: Maintenance, Repairs, AMC or a specific Event.</p>
        <form
          className="finance-form"
          onSubmit={(e) => {
            e.preventDefault();
            save('funds', fund, () => {
              setFund({
                code: '',
                name: '',
                fundType: 'MAINTENANCE',
                accountingUnitId: '',
                description: '',
                active: true,
              });
              setEditingFundId(null);
            }, editingFundId);
          }}
        >
          <Field label="Accounting unit">
            <select
              required
              value={fund.accountingUnitId}
              onChange={(e) =>
                setFund({ ...fund, accountingUnitId: e.target.value })
              }
            >
              <option value="">Select</option>
              {foundation.accountingUnits.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Code">
            <input
              required
              value={fund.code}
              onChange={(e) => setFund({ ...fund, code: e.target.value })}
            />
          </Field>
          <Field label="Name">
            <input
              required
              value={fund.name}
              onChange={(e) => setFund({ ...fund, name: e.target.value })}
            />
          </Field>
          <Field label="Fund type" help="Classify the fund for reports and selection, for example Maintenance, Event, Repair or Emergency.">
            <select value={fund.fundType} onChange={(e) => setFund({ ...fund, fundType: e.target.value })}>
              {['MAINTENANCE', 'EVENT', 'REPAIR', 'COMMON', 'EMERGENCY', 'OTHER'].map((value) => <option key={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea value={fund.description} onChange={(e) => setFund({ ...fund, description: e.target.value })} />
          </Field>
          <label className="finance-check"><input type="checkbox" checked={fund.active} onChange={(e) => setFund({ ...fund, active: e.target.checked })} /> Active</label>
          <button className="btn btn-primary">{editingFundId ? 'Update fund' : 'Add fund'}</button>
        </form>
        {foundation.funds.map((x) => (
          <div key={x.id} className="finance-setup-item">
            <span><strong>{x.name}</strong><small>{x.fund_type}</small></span>
            <div className="finance-actions">
              <button type="button" onClick={() => { setEditingFundId(x.id); setFund({ code: x.code, name: x.name, description: x.description || '', fundType: x.fund_type, accountingUnitId: String(x.accounting_unit_id), active: x.active }); }}>Edit</button>
              <button type="button" className="danger" onClick={() => removeItem('funds', x.id, x.name, () => setEditingFundId(null))}>Delete</button>
            </div>
          </div>
        ))}
      </Section>
      <Section title="3. Receiving accounts">
        <p className="finance-help">Where will residents pay? Add the community bank account or UPI destination. MyCommunity never holds the money.</p>
        <form
          className="finance-form"
          onSubmit={(e) => {
            e.preventDefault();
            save('transaction-accounts', account, () => {
              setAccount({
                ...account,
                name: '',
                upiId: '',
                accountNumber: '',
                maskedAccountNumber: '',
                maskAccountNumber: false,
                ifsc: '',
                bankName: '',
                accountHolderName: '',
                upiPayeeName: '',
              });
              setEditingAccountId(null);
            }, editingAccountId);
          }}
        >
          <Field label="Account type" help="Choose BANK or CASH according to where the community actually receives and holds this money.">
            <select value={account.accountType} onChange={(e) => setAccount({ ...account, accountType: e.target.value })}>
              {['BANK', 'UPI', 'CASH', 'OTHER'].map((value) => <option key={value}>{value}</option>)}
            </select>
          </Field>
          <Field label="Accounting unit">
            <select
              required
              value={account.accountingUnitId}
              onChange={(e) =>
                setAccount({
                  ...account,
                  accountingUnitId: e.target.value,
                  fundId: '',
                })
              }
            >
              <option value="">Select</option>
              {foundation.accountingUnits.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Fund">
            <select
              value={account.fundId}
              onChange={(e) =>
                setAccount({ ...account, fundId: e.target.value })
              }
            >
              <option value="">Any fund</option>
              {foundation.funds
                .filter(
                  (x) =>
                    String(x.accounting_unit_id) ===
                    String(account.accountingUnitId)
                )
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Account name">
            <input
              required
              value={account.name}
              onChange={(e) => setAccount({ ...account, name: e.target.value })}
            />
          </Field>
          <div className="finance-two">
            <Field label="Bank name"><input value={account.bankName} onChange={(e) => setAccount({ ...account, bankName: e.target.value })} /></Field>
            <Field label="Account-holder name"><input value={account.accountHolderName} onChange={(e) => setAccount({ ...account, accountHolderName: e.target.value })} /></Field>
          </div>
          <Field label="UPI ID">
            <input
              value={account.upiId}
              onChange={(e) =>
                setAccount({ ...account, upiId: e.target.value })
              }
            />
          </Field>
          <Field label="UPI payee name"><input value={account.upiPayeeName} onChange={(e) => setAccount({ ...account, upiPayeeName: e.target.value })} /></Field>
          <Field label="Bank account number" help="Enter the complete receiving account number. It is shown only to authenticated residents who are eligible for this collection.">
              <input
              required={account.accountType === 'BANK'}
              value={account.accountNumber}
              onChange={(e) =>
                setAccount({ ...account, accountNumber: e.target.value })
              }
            />
              {account.accountType === 'BANK' && <small>Required for NEFT, IMPS, RTGS and bank transfers.</small>}
          </Field>
          <label className="finance-check">
            <input
              type="checkbox"
              checked={account.maskAccountNumber}
              onChange={(e) => setAccount({ ...account, maskAccountNumber: e.target.checked })}
            />
            Mask account number for residents
          </label>
          <small className="finance-help">
            {account.maskAccountNumber
              ? 'Residents will see only the last four digits. Administrators can still edit the complete number.'
              : 'Eligible residents will see the complete account number for NEFT, IMPS, RTGS and bank transfers.'}
          </small>
          <Field label="IFSC"><input value={account.ifsc} onChange={(e) => setAccount({ ...account, ifsc: e.target.value })} /></Field>
          <fieldset className="finance-targets">
            <legend><FinanceFieldLabel label="Supported payment modes" help="Select only the payment methods residents may use for this destination account." /></legend>
            {(foundation.paymentModes || []).map((mode) => (
              <label key={mode}><input type="checkbox" checked={account.supportedPaymentModes.includes(mode)} onChange={(e) => setAccount({ ...account, supportedPaymentModes: e.target.checked ? [...account.supportedPaymentModes, mode] : account.supportedPaymentModes.filter((item) => item !== mode) })} />{mode}</label>
            ))}
          </fieldset>
          <div className="finance-two">
            <Field label="Effective from" help="Optional first date on which residents may use this account."><input type="date" value={account.effectiveFrom} onChange={(e) => setAccount({ ...account, effectiveFrom: e.target.value })} /></Field>
            <Field label="Effective to" help="Optional last date on which residents may use this account."><input type="date" value={account.effectiveTo} onChange={(e) => setAccount({ ...account, effectiveTo: e.target.value })} /></Field>
          </div>
          <label className="finance-check"><input type="checkbox" checked={account.active} onChange={(e) => setAccount({ ...account, active: e.target.checked })} /> Active</label>
          <button className="btn btn-primary">{editingAccountId ? 'Update receiving account' : 'Add receiving account'}</button>
        </form>
        {foundation.transactionAccounts.map((item) => (
          <div key={item.id} className="mt-3 finance-account-card">
            <strong>{item.name}</strong>
            <dl className="finance-detail-grid">
              <div><dt>Bank</dt><dd>{item.bank_name || 'Not applicable'}</dd></div>
              <div><dt>Account holder</dt><dd>{item.account_holder_name || item.upi_payee_name || 'Not configured'}</dd></div>
              <div><dt>Account number</dt><dd>{item.account_number || item.masked_account_number || 'Not configured'}</dd></div>
              <div><dt>Resident display</dt><dd>{item.mask_account_number ? `Masked (${item.masked_account_number})` : 'Complete account number'}</dd></div>
              <div><dt>IFSC</dt><dd>{item.ifsc || 'Not configured'}</dd></div>
              <div><dt>UPI ID</dt><dd>{item.upi_id || 'Not configured'}</dd></div>
              <div><dt>Status</dt><dd>{item.active ? 'Active' : 'Inactive'}</dd></div>
            </dl>
            {item.has_qr && (
              <ProtectedImage
                url={`/api/finance/transaction-accounts/${item.id}/qr-code`}
                alt={`Payment QR for ${item.name}`}
              />
            )}
            <div className="finance-actions">
              <button type="button" onClick={() => { setEditingAccountId(item.id); setAccount({ ...account, name: item.name, accountType: item.account_type || 'BANK', accountingUnitId: String(item.accounting_unit_id), fundId: item.fund_id ? String(item.fund_id) : '', bankName: item.bank_name || '', accountHolderName: item.account_holder_name || '', upiId: item.upi_id || '', upiPayeeName: item.upi_payee_name || '', accountNumber: item.account_number || '', maskedAccountNumber: item.masked_account_number || '', maskAccountNumber: Boolean(item.mask_account_number), ifsc: item.ifsc || '', effectiveFrom: item.effective_from || '', effectiveTo: item.effective_to || '', active: item.active, supportedPaymentModes: foundation.accountPaymentModes.filter((m) => m.transaction_account_id === item.id).map((m) => m.payment_mode) }); }}>Edit</button>
              <button type="button" className="danger" onClick={() => removeItem('transaction-accounts', item.id, item.name, () => setEditingAccountId(null))}>Delete</button>
            </div>
            <label className="finance-field mt-1">
              <FinanceFieldLabel label="Payment QR (PNG/JPEG)" />
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(event) => uploadQr(item.id, event.target.files?.[0])}
              />
              <small>{item.has_qr ? `Configured: ${item.qr_code_original_name || 'payment QR'}` : 'No payment QR configured.'}</small>
            </label>
          </div>
        ))}
      </Section>
      <Section title="4. Treasurer and finance scopes">
        <p className="finance-help">Limit a treasurer, accountant or auditor to one accounting area or one fund. Their action permissions are still managed in Access Control.</p>
        <form
          className="finance-form"
          onSubmit={(e) => {
            e.preventDefault();
            save('scope-assignments', scope, () => {
              setScope({ userId: '', accountingUnitId: '', fundId: '', active: true });
              setEditingScopeId(null);
            }, editingScopeId);
          }}
        >
          <Field label="Finance user" help="Choose the community user whose financial access should be restricted to the selected area or fund.">
            <select required value={scope.userId} onChange={(e) => setScope({ ...scope, userId: e.target.value })}>
              <option value="">Select user</option>
              {users.map((user) => <option key={user.id} value={user.id}>{user.name} · {user.email}</option>)}
            </select>
          </Field>
          <Field label="Accounting unit">
            <select required value={scope.accountingUnitId} onChange={(e) => setScope({ ...scope, accountingUnitId: e.target.value, fundId: '' })}>
              <option value="">Select</option>
              {foundation.accountingUnits.filter((item) => item.active).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <Field label="Fund">
            <select value={scope.fundId} onChange={(e) => setScope({ ...scope, fundId: e.target.value })}>
              <option value="">All funds in this accounting area</option>
              {foundation.funds.filter((item) => String(item.accounting_unit_id) === String(scope.accountingUnitId)).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </Field>
          <label className="finance-check"><input type="checkbox" checked={scope.active} onChange={(e) => setScope({ ...scope, active: e.target.checked })} /> Active</label>
          <button className="btn btn-primary">{editingScopeId ? 'Update finance scope' : 'Assign finance scope'}</button>
        </form>
        {foundation.scopeAssignments.map((item) => (
          <div key={item.id} className="finance-setup-item">
            <span><strong>{item.user_name}</strong><small>{item.accounting_unit_name} · {item.fund_name || 'All funds'}</small></span>
            <div className="finance-actions">
              <button type="button" onClick={() => { setEditingScopeId(item.id); setScope({ userId: String(item.user_id), accountingUnitId: String(item.accounting_unit_id), fundId: item.fund_id ? String(item.fund_id) : '', active: item.active }); }}>Edit</button>
              <button type="button" className="danger" onClick={() => removeItem('scope-assignments', item.id, `${item.user_name}'s finance scope`, () => setEditingScopeId(null))}>Delete</button>
            </div>
          </div>
        ))}
      </Section>
    </div>
    </>
  );
}
function ProtectedImage({ url, alt, onOpen }) {
  const [source, setSource] = useState('');
  const [isImage, setIsImage] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl = '';
    api.get(url, { responseType: 'blob' })
      .then((response) => {
        if (!active) return;
        setIsImage(response.data?.type?.startsWith('image/'));
        objectUrl = URL.createObjectURL(response.data);
        setSource(objectUrl);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  if (failed) return <small>Attachment preview is unavailable.</small>;
  if (!source) return <small>Loading attachment…</small>;
  if (!isImage)
    return <button type="button" onClick={onOpen || (() => window.open(source, '_blank'))}>Open payment proof</button>;
  return (
    <button type="button" className="finance-proof" onClick={onOpen || (() => window.open(source, '_blank'))}>
      <img src={source} alt={alt} />
      <span>Open full size</span>
    </button>
  );
}

function PaymentTimeline({ items }) {
  return (
    <ol className="finance-payment-timeline">
      {(items || []).map((item) => (
        <li key={item.id}>
          <strong>{String(item.action || '').replaceAll('_', ' ')}</strong>
          <span>{item.actor_name || item.actor_username} · {dateTime(item.created_at)}</span>
          {item.comments && <p>{item.comments}</p>}
          {typeof item.details === 'string' && item.details.trim() && <p className="finance-history-snapshot">{item.details}</p>}
          {Number(item.has_attachment) > 0 && (
            <ProtectedImage
              url={`/api/finance/payment-submissions/${item.submission_id || ''}/history/${item.id}/evidence`}
              alt={item.attachment_original_name || 'Historical payment proof'}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

function PaymentVerificationDetail({ item, history, onBack, onLoadHistory, onReview, onEvidence }) {
  const pending = ['PAYMENT_SUBMITTED', 'UNDER_VERIFICATION'].includes(item.verification_status);
  const [historyOpen, setHistoryOpen] = useState(false);
  const toggleHistory = async () => {
    if (!historyOpen && !history.length) await onLoadHistory();
    setHistoryOpen((current) => !current);
  };
  return (
    <div className="finance-verification-detail">
      <div className="finance-actions">
        <button type="button" onClick={onBack}>← Back to payment register</button>
      </div>
      <header className="finance-detail-header">
        <div><small>Resident</small><h3>{item.resident_name}</h3><span>{item.resident_email || item.resident_mobile}</span></div>
        <div><span className="finance-status">{item.verification_status}</span><h3>{money(item.amount_submitted)}</h3></div>
      </header>
      <dl className="finance-detail-grid">
        <div><dt>Collection</dt><dd>{item.campaign_title}</dd></div>
        <div><dt>Division / wing</dt><dd>{item.division_name || item.division_code || 'Community root'}</dd></div>
        <div><dt>Property / unit</dt><dd>{item.property_name || item.property_number}</dd></div>
        <div><dt>Submitted at (IST)</dt><dd>{dateTime(item.submitted_at)}</dd></div>
        <div><dt>Payment account</dt><dd>{item.account_name || 'Not recorded'}<small>{item.upi_id || item.masked_account_number || item.account_bank_name}</small></dd></div>
        <div><dt>Payment mode</dt><dd>{item.payment_mode}</dd></div>
        <div><dt>Transaction reference</dt><dd>{item.transaction_reference || item.cheque_number || item.cash_voucher_number || 'Not supplied'}</dd></div>
        <div><dt>Payer</dt><dd>{item.payer_name || 'Not supplied'}</dd></div>
        <div><dt>Resident remarks</dt><dd>{item.resident_remarks || 'No remarks'}</dd></div>
        <div><dt>Administrator remarks</dt><dd>{item.reviewer_remarks || 'No remarks'}</dd></div>
      </dl>
      <h4>Latest submitted proof</h4>
      {item.has_evidence ? (
        <ProtectedImage url={`/api/finance/payment-submissions/${item.id}/evidence`} alt={`Payment proof from ${item.resident_name}`} onOpen={() => onEvidence(item)} />
      ) : <p className="finance-review-note warning">No payment proof attached.</p>}
      {pending && (
        <div className="finance-actions">
          <button onClick={() => onReview(item.id, item.payment_mode === 'CHEQUE' && item.cheque_status !== 'DEPOSITED' ? 'APPROVE' : item.payment_mode === 'CHEQUE' ? 'CLEAR' : 'APPROVE')}>
            {item.payment_mode === 'CHEQUE' && item.cheque_status !== 'DEPOSITED' ? 'Mark deposited' : item.payment_mode === 'CHEQUE' ? 'Mark cleared' : 'Approve'}
          </button>
          {item.payment_mode === 'CHEQUE' && <button className="danger" onClick={() => onReview(item.id, 'BOUNCE')}>Mark bounced</button>}
          <button onClick={() => onReview(item.id, 'CORRECTION_REQUESTED')}>Request correction</button>
          <button className="danger" onClick={() => onReview(item.id, 'REJECT')}>Reject</button>
        </div>
      )}
      <h4>Complete submission history</h4>
      <p className="finance-help">Every submission, correction, remark and uploaded attachment is retained as a separate version.</p>
      <button type="button" className="finance-history-toggle" onClick={toggleHistory}>
        {historyOpen ? 'Hide submission history' : 'View submission history'}
      </button>
      {historyOpen && <PaymentTimeline items={history} />}
    </div>
  );
}

function ChargeGenerationPreview({ preview, onCancel, onGenerate }) {
  const { campaign, targets = [] } = preview;
  const adjustable = ['CUSTOM_PER_UNIT', 'MANUAL_ASSIGNMENT'].includes(campaign.collection_mode);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const editableIds = useMemo(() => targets.filter((x) => Number(x.existing_paid || 0) === 0).map((x) => Number(x.id)), [targets]);
  const hasExistingAssignments = useMemo(() => targets.some((x) => Boolean(x.existing_charge_id)), [targets]);
  const initiallyAssigned = useMemo(
    () => targets
      .filter((x) => !hasExistingAssignments || (x.existing_charge_id && x.existing_charge_status !== 'CANCELLED'))
      .map((x) => Number(x.id)),
    [targets, hasExistingAssignments]
  );
  const [selected, setSelected] = useState(() => initiallyAssigned);
  const [amounts, setAmounts] = useState(() => Object.fromEntries(targets.map((item) => [Number(item.id), item.existing_payable || campaign.amount_per_unit || ''])));
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return targets.filter((item) => {
      const assigned = item.existing_charge_id && item.existing_charge_status !== 'CANCELLED';
      if (status === 'ASSIGNED' && !assigned) return false;
      if (status === 'UNASSIGNED' && assigned) return false;
      return !term || [item.structure_unit_code, item.structure_unit_name, item.property_number, item.display_name, item.resident_name, item.existing_charge_number].some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [targets, search, status]);
  const pageItems = filtered.slice(page * size, page * size + size);
  const totalPages = Math.max(1, Math.ceil(filtered.length / size));
  const selectablePageIds = pageItems.filter((x) => editableIds.includes(Number(x.id))).map((x) => Number(x.id));
  const submit = () => {
    const customAmounts = {};
    if (adjustable) {
      for (const id of selected) {
        const amount = Number(amounts[id]);
        if (!Number.isFinite(amount) || amount <= 0) {
          window.alert('Enter a positive amount for every selected property.');
          return;
        }
        customAmounts[id] = amount;
      }
    }
    onGenerate(selected, customAmounts);
  };
  return (
    <Section title="Review scoped charges">
      <div className="finance-charge-summary">
        <strong>{campaign.title}</strong>
        <span>{campaign.billing_scope} scope · {selected.length} assigned · {targets.length - selected.length} not assigned</span>
        <p>Check or uncheck unpaid homes and save the assignment. Homes with an approved or partially paid charge remain selected and locked to preserve financial history.</p>
      </div>
      <div className="finance-verification-filters">
        <Field label="Search"><input value={search} placeholder="Division, property, resident or charge" onChange={(e) => { setPage(0); setSearch(e.target.value); }} /></Field>
        <Field label="Assignment status"><select value={status} onChange={(e) => { setPage(0); setStatus(e.target.value); }}><option value="ALL">All scoped homes</option><option value="ASSIGNED">Assigned</option><option value="UNASSIGNED">Not assigned</option></select></Field>
        <Field label="Rows per page"><select value={size} onChange={(e) => { setPage(0); setSize(Number(e.target.value)); }}>{[5, 10, 20, 50].map((value) => <option key={value}>{value}</option>)}</select></Field>
      </div>
      <div className="finance-actions">
        <button type="button" onClick={() => setSelected(Array.from(new Set([...selected, ...selectablePageIds])))}>Select page</button>
        <button type="button" onClick={() => setSelected(selected.filter((id) => !selectablePageIds.includes(id)))}>Clear page</button>
      </div>
      <div className="table-responsive finance-verification-register finance-mobile-cards">
        <table className="table">
          <thead><tr><th>Select</th><th>Division / wing</th><th>Property</th><th>Resident</th><th>Amount</th><th>Status</th></tr></thead>
          <tbody>
            {pageItems.map((item) => {
              const id = Number(item.id);
              const locked = Number(item.existing_paid || 0) > 0 || ['PAID', 'PARTIALLY_PAID'].includes(item.existing_charge_status);
              const assigned = item.existing_charge_id && item.existing_charge_status !== 'CANCELLED';
              return <tr key={item.id}>
                <td data-label="Select"><input type="checkbox" disabled={locked} checked={selected.includes(id)} onChange={(e) => setSelected(e.target.checked ? [...selected, id] : selected.filter((value) => value !== id))} /></td>
                <td data-label="Division / wing">{item.structure_unit_name || item.structure_unit_code || 'Community root'}</td>
                <td data-label="Property">{item.display_name || item.property_number}</td>
                <td data-label="Resident">{item.resident_name || 'Unassigned home'}</td>
                <td data-label="Amount">{adjustable && !locked ? <input className="finance-amount-input" type="number" min="0.01" step="0.01" value={amounts[id] || ''} onChange={(e) => setAmounts({ ...amounts, [id]: e.target.value })} /> : money(item.existing_payable || campaign.amount_per_unit)}</td>
                <td data-label="Status">{locked ? `${item.existing_charge_status} · locked` : assigned ? `${item.existing_charge_status} · assigned` : 'Not assigned'}</td>
              </tr>;
            })}
            {!pageItems.length && <tr><td colSpan="6">No scoped properties match these filters.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="finance-report-pagination">
        <span>{selected.length} selected · Page {page + 1} of {totalPages}</span>
        <div><button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button><button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>Next</button></div>
      </div>
      <div className="finance-actions">
        <button type="button" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={submit}>{campaign.contribution_type === 'VOLUNTARY' ? 'Publish to selected homes' : `Save assignments (${selected.length})`}</button>
      </div>
    </Section>
  );
}

function Section({ title, children }) {
  return (
    <section className="finance-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}
function Field({ label, help, children }) {
  return (
    <label className="finance-field">
      <FinanceFieldLabel label={label} help={help} />
      {children}
    </label>
  );
}
function Metric({ label, value, metricKey, breakdown = [], currency = false }) {
  const [expanded, setExpanded] = useState(false);
  const [openUnits, setOpenUnits] = useState({});
  const units = useMemo(() => {
    const grouped = new Map();
    breakdown.forEach((row) => {
      const id = String(row.accounting_unit_id);
      if (!grouped.has(id)) {
        grouped.set(id, {
          id,
          name: row.accounting_unit_name,
          total: 0,
          funds: [],
        });
      }
      const amount = Number(row[metricKey] || 0);
      const unit = grouped.get(id);
      unit.total += amount;
      unit.funds.push({
        id: row.fund_id,
        name: row.fund_name,
        value: amount,
      });
    });
    return [...grouped.values()].filter((unit) => unit.total !== 0);
  }, [breakdown, metricKey]);
  const display = (amount) => (currency ? money(amount) : amount);
  return (
    <article className={`finance-metric ${expanded ? 'expanded' : ''}`}>
      <button
        type="button"
        className="finance-metric-summary"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
      >
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{expanded ? 'Hide breakdown' : 'View unit & fund breakdown'} <b>{expanded ? '−' : '+'}</b></small>
      </button>
      {expanded && (
        <div className="finance-metric-tree">
          {units.map((unit) => (
            <div key={unit.id} className="finance-tree-unit">
              <button type="button" onClick={() => setOpenUnits((current) => ({ ...current, [unit.id]: !current[unit.id] }))}>
                <span>{openUnits[unit.id] ? '▾' : '▸'} {unit.name}</span>
                <strong>{display(unit.total)}</strong>
              </button>
              {openUnits[unit.id] && (
                <ul>
                  {unit.funds.filter((fund) => fund.value !== 0).map((fund) => (
                    <li key={fund.id}>
                      <span>{fund.name}</span>
                      <strong>{display(fund.value)}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {!units.length && <p>No unit or fund activity for this metric.</p>}
        </div>
      )}
    </article>
  );
}
function CampaignTable({ items = [], onGenerate, onEdit, onStatus, onDelete, onNextCycle }) {
  return (
    <div className="finance-cards">
      {items.map((x) => (
        <article key={x.id}>
          <header>
            <strong>{x.title}</strong>
            <span>{x.status}</span>
          </header>
          <p>
            {x.collection_type} · {x.accounting_unit_name}
          </p>
          <h3>
            {x.amount_per_unit ? money(x.amount_per_unit) : x.collection_mode}
          </h3>
          <small>
            {x.contribution_type === 'VOLUNTARY'
              ? 'Voluntary'
              : 'Compulsory'}{' '}
            · {String(x.billing_scope || 'COMMUNITY').toLowerCase()} · Due{' '}
            {x.due_date || 'not specified'} · {x.fund_name}
          </small>
          <div className="finance-actions">
            {onEdit && x.status === 'DRAFT' && <button onClick={() => onEdit(x)}>Edit</button>}
            {onGenerate && ['DRAFT', 'ACTIVE'].includes(x.status) && (
              <button onClick={() => onGenerate(x)}>
                {x.contribution_type === 'VOLUNTARY'
                  ? 'Publish contribution'
                  : 'Generate scoped charges'}
              </button>
            )}
            {onStatus && x.status === 'ACTIVE' && <button onClick={() => onStatus(x, 'CLOSED')}>Close</button>}
            {onNextCycle && ['ACTIVE', 'CLOSED'].includes(x.status) && (
              <button onClick={() => onNextCycle(x)}>Create next cycle</button>
            )}
            {onStatus && ['DRAFT', 'ACTIVE'].includes(x.status) && <button className="danger" onClick={() => onStatus(x, 'CANCELLED')}>Cancel</button>}
            {onDelete && x.status === 'DRAFT' && <button className="danger" onClick={() => onDelete(x)}>Delete</button>}
          </div>
        </article>
      ))}
    </div>
  );
}
function Pager({ data, page, setPage }) {
  return data?.totalPages > 1 ? (
    <Pagination
      currentPage={page + 1}
      totalPages={data.totalPages}
      onPageChange={(p) => setPage(p - 1)}
    />
  ) : null;
}
